import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators
} from '@angular/forms';
import { TuiAlertService } from '@taiga-ui/core';
import { tuiCreateTimePeriods } from '@taiga-ui/kit';
import { TuiCountryIsoCode } from '@taiga-ui/i18n';
import { NgbOffcanvas, NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';

import {
  Client,
  GetClientsResponse,
  GetServiceDetailProviderResponse,
  ProviderServiceDetail,
  ServiceConfiguration,
  ServiceDetail
} from 'src/app/models/service-detail/service-detail.model';
import { Zone, ZonesResponse } from 'src/app/models/zone/zone.model';
import {
  vehicleChassisType,
  vehicleChassisTypeTranslated,
  vehicleFuelType,
  vehicleFuelTypeTranslated,
  verticalTypes3
} from 'src/app/constants/data';
import { VehicleFuelType } from 'src/app/models/vehicle-fuel-type/vehicle-fuel-type.model';
import { VerticalTypeTwo } from 'src/app/models/vertical-type/vertical-type.model';
import { HistoryServicesService } from 'src/app/services/history-services/history-services.service';
import { TuiTime } from '@taiga-ui/cdk';
import {
  GetProductsResponse,
  ProductUi
} from 'src/app/models/product/product.model';
import { TranslateService } from '@ngx-translate/core';
import { PermissionsService } from 'src/app/services/permissions/permissions.service';
import {
  catchError,
  debounceTime,
  delay,
  distinctUntilChanged,
  EMPTY,
  filter,
  finalize,
  forkJoin,
  map,
  merge,
  of,
  startWith,
  Subject,
  switchMap,
  take,
  takeUntil,
  tap,
  throwError
} from 'rxjs';

import * as dayjs from 'dayjs';
import type { PluginFunc } from 'dayjs';
import * as utc from 'dayjs/plugin/utc';
import * as timezone from 'dayjs/plugin/timezone';
import {
  AddressPoint,
  ContactInformation,
  CreateServiceRequestDto,
  ServiceDate,
  UpdateOrderRequestDto
} from 'src/app/models/service/service.model';
import { VehicleChassisType } from 'src/app/models/selector/selector.model';
import { TransferTypeEnum } from 'src/app/enum/transfer-type.enum';
import { VehicleChassisTypeEnum } from 'src/app/enum/vehicle-chassis-type.enum';
import { VehicleFuelTypeEnum } from 'src/app/enum/vehicle-fuel.enum';
import { redTrashIcon } from 'src/app/utils/icons-path';
import { OperationResult } from 'src/app/models/common/common.model';
import { strictEmailValidator } from 'src/app/validators/form-validators';
import { phoneValidatorV2 } from 'src/app/utils/phone-validator-v2';
import { getCountryIsoFromPhone } from 'src/app/utils/phone-utils';
import {
  AutoKey,
  HourType,
  ModalMode,
  ServiceForm
} from './model/modal-duplicate-service.model';
import { getCurrencySymbolByZoneId } from 'src/app/utils/currency-symbol.helper';
import { ZoneIdType } from 'src/app/enum/zones-id.type.enum';
import { ServiceOptionType } from 'src/app/enum/service-options.enum';
import { ModalUploadDeliveryNoteComponent } from 'src/app/shared/modal-upload-delivery-note/modal-upload-delivery-note.component';
import { ModalConfirmComponent } from 'src/app/shared/modal-confirm/modal-confirm.component';

/* Extiende Day.js (cast rápido para contentar al tipado) */
(dayjs.extend as (p: PluginFunc) => dayjs.Dayjs)(utc as unknown as PluginFunc);
(dayjs.extend as (p: PluginFunc) => dayjs.Dayjs)(
  timezone as unknown as PluginFunc
);

declare const google: any;
declare const moment: any;
const TIME_24H_REGEX = /^([01]\d|2[0-3]):([0-5]\d)$/;
const ZERO_GUID = '00000000-0000-0000-0000-000000000000';
const ALIGNMENT_COST = 25000;
const EXCHANGE_OLD_COST = 20000;

/**
 * Nombre con el que el back manda el chasis de los servicios creados sin elegirlo
 * (p.ej. la landing de COPEC, donde no se pregunta). No está en `vehicleChassisType`
 * a propósito: no debe poder elegirse al dar de alta un servicio.
 */
const UNDEFINED_CHASSIS_NAME = 'UNDEFINED';

/**
 * Las medidas e índices del neumático son enteros y el coste un decimal, pero los campos
 * son de texto. Sin estos patrones se podían teclear letras: al construir el payload
 * `+'AA'` da NaN, que `JSON.stringify` serializa como `null`, y la medida se perdía por el
 * camino sin que nadie avisara.
 */
const TYRE_INTEGER_PATTERN = /^\d+$/;
const TYRE_DECIMAL_PATTERN = /^\d+([.,]\d+)?$/;

/**
 * El índice de velocidad es una letra por norma (91V, 103H). Y tiene que serlo: al guardar,
 * carga y velocidad se concatenan en la medida como `44V`, y lo que separa una de otra al
 * releerla es justamente que la segunda no sea un dígito. Con un índice numérico la cadena
 * queda ambigua, los dos índices se pierden y el sobrante acaba dentro del modelo.
 */
const TYRE_SPEED_INDEX_PATTERN = /^[A-Za-z]+$/;

/**
 * Opción de solo lectura para poder pintar un chasis UNDEFINED en el desplegable.
 * Se añade únicamente al editar (donde el campo está bloqueado), nunca al alta
 * ni al duplicado.
 */
const UNDEFINED_CHASSIS_OPTION: VehicleChassisType = {
  value: VehicleChassisTypeEnum.UNDEFINED,
  name: UNDEFINED_CHASSIS_NAME,
  real_name: 'unknown'
};

@Component({
  selector: 'app-modal-duplicate-service',
  templateUrl: './modal-duplicate-service.component.html',
  styleUrls: ['./modal-duplicate-service.component.css']
})
export class ModalDuplicateServiceComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  @ViewChild('originAddress', { static: true })
  originField!: ElementRef<HTMLInputElement>;
  @ViewChild('destinationAddress', { static: true })
  destinationField!: ElementRef<HTMLInputElement>;
  @ViewChild('tyresAddress', { static: true })
  tyresField!: ElementRef<HTMLInputElement>;
  @ViewChild('providerAddress', { static: true })
  providerField!: ElementRef<HTMLInputElement>;
  modalMode = ModalMode;

  @Input({ required: true }) type_info: ModalMode = ModalMode.Add;
  @Input() service_info?: ServiceDetail | null;
  @Input() currency: string = '€';
  @Output() finished = new EventEmitter<boolean>();

  readonly countriesPhone: ReadonlyArray<TuiCountryIsoCode> = [
    TuiCountryIsoCode.ES,
    TuiCountryIsoCode.GB,
    TuiCountryIsoCode.FR,
    TuiCountryIsoCode.IT,
    TuiCountryIsoCode.DE,
    TuiCountryIsoCode.PT,
    TuiCountryIsoCode.NL,
    TuiCountryIsoCode.AD,
    TuiCountryIsoCode.CL,
    TuiCountryIsoCode.DO
  ];

  // No es `readonly` porque al editar un servicio sin chasis se le añade la opción
  // de solo lectura UNDEFINED (ver `extractVehicleData`).
  VehicleChassisType: VehicleChassisType[] = vehicleChassisTypeTranslated;
  readonly VehicleFuelType: VehicleFuelType[] = vehicleFuelTypeTranslated;

  readonly verticalsTypes: VerticalTypeTwo[] = verticalTypes3;
  readonly monetarys: number[] = [5, 10, 20, 50, 80];
  readonly TYRESVERTICAL: number = 12;
  readonly REFUELINGVERTICAL: number = 4;
  readonly ITVVERTICAL: number = 7;

  zones: Zone[] = [];
  clients: Client[] = [];
  providers: ProviderServiceDetail[] = [];
  providersName: string[] = [];
  productsName: string[] = [];
  productsOfVertical: ProductUi[] = [];
  verticals: string[] = []; // verticales disponibles según filtros

  countryIsoCode = TuiCountryIsoCode.ES;
  countryIsoCode2 = TuiCountryIsoCode.ES;

  redTrashIcon = redTrashIcon;

  showProvider: boolean = false;
  activeVertical: string | null = null;
  showGarageAddr: boolean = false;

  zonesLoaded: boolean = false;
  clientsLoaded: boolean = false;
  loadingProducts: boolean = false;

  isSaving: boolean = false;
  providersLoading: boolean = false;
  openElement: EventTarget | string | null = null;

  hours2: readonly TuiTime[] = [];

  readonly stringifyNameclient = (c: Client): string => c.businessName;
  readonly stringifyFuel = (f: string): string => f;
  readonly stringifyMoney = (v: string): string => v;

  form!: FormGroup;

  hourTypes: { value: string; label: string }[] = [
    {
      value: 'Franja horaria',
      label: this.translate.instant('modals.add_service.franje_hour_input')
    },
    {
      value: 'Hora fija',
      label: this.translate.instant('modals.add_service.fixed_hour_input')
    }
  ];

  activeVerticalValue: number | null = null;
  activeProductValue: number | null = null;
  isCopec = false;
  isOkMobilityTenant = false;

  private autocompleteListeners: google.maps.MapsEventListener[] = [];
  private readonly destroy$ = new Subject<void>();

  private prevDestinationContactState: {
    destinationContactName?: string;
    destinationContactPhoneNumber?: string;
    destinationContactEmailAddress?: string;
    isDestinationContactExternal?: boolean;
    destinationContactPhoneIso?: TuiCountryIsoCode;
  } = {};

  readonly countryCodes = [
    TuiCountryIsoCode.ES,
    TuiCountryIsoCode.GB,
    TuiCountryIsoCode.FR,
    TuiCountryIsoCode.IT,
    TuiCountryIsoCode.DE,
    TuiCountryIsoCode.PT,
    TuiCountryIsoCode.NL,
    TuiCountryIsoCode.AD,
    TuiCountryIsoCode.CL,
    TuiCountryIsoCode.DO
  ] as const;

  providerPlaceholder: string = this.translate.instant(
    'modals.add_service.select_provider'
  );

  deliveryNoteId: string = '';
  deliveryNoteName: string = '';
  deliveryNoteSize: string = '';
  deliveryNoteDate: string = '';

  /* ------------------------- inyección de servicios ---------------------- */

  constructor(
    private fb: FormBuilder,
    private hs: HistoryServicesService,
    private alerts: TuiAlertService,
    private off: NgbOffcanvas,
    private ngZone: NgZone,
    private translate: TranslateService,
    private cd: ChangeDetectorRef,
    private permissionsService: PermissionsService,
    private modalService: NgbModal
  ) {}

  ngOnInit(): void {
    this.buildForm();
    this.loadInitialData();
    this.listenFormChanges();
    this.permissionsService
      .isCopec()
      .pipe(takeUntil(this.destroy$))
      .subscribe((isCopec) => {
        this.isCopec = isCopec;
        if (isCopec && this.currency === '€') {
          this.currency = 'CLP';
        }
        this.cd.detectChanges();
      });

    this.permissionsService
      .isOkMobility()
      .pipe(takeUntil(this.destroy$))
      .subscribe((isOkMobility) => {
        const changed = this.isOkMobilityTenant !== isOkMobility;
        this.isOkMobilityTenant = isOkMobility;

        if (changed) {
          this.reloadProducts();
        }

        this.cd.detectChanges();
      });
  }

  ngAfterViewInit(): void {
    (['origin', 'destination', 'tyres', 'provider'] as const).forEach((k) =>
      this.initAutocomplete(k)
    );
  }

  private uppercaseLicensePlate(): void {
    this.form
      .get('licensePlate')!
      .valueChanges.pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((v: string) => {
        const upper = v?.toUpperCase() ?? '';
        if (v !== upper) {
          this.form.get('licensePlate')!.setValue(upper, { emitEvent: false });
        }
      });
  }

  private chassisValueFromBackend(name: string): number | null {
    return vehicleChassisType.find((c) => c.name === name)?.value ?? null;
  }

  private fuelValueFromBackend(name: string): number | null {
    return vehicleFuelType.find((f) => f.name === name)?.value ?? null;
  }

  private loadInitialData(): void {
    this.zonesLoaded = true;
    this.clientsLoaded = true;
    forkJoin({
      zones: this.hs.getZones().pipe(
        map((r: ZonesResponse) => r.zones),
        catchError((err) => {
          this.zonesLoaded = false;
          console.error('Error cargando zonas', err);
          this.alerts
            .open(
              this.translate.instant('modals.add_service.load_zones') ||
                'Error al cargar zonas',
              { status: 'error' }
            )
            .subscribe();
          return of([] as Zone[]);
        })
      ),
      clients: this.hs.getClients().pipe(
        map((r: GetClientsResponse) => r.businessUserData),
        catchError((err) => {
          this.clientsLoaded = false;
          console.error('Error cargando clientes', err);
          this.alerts
            .open(
              this.translate.instant('modals.add_service.load_clients') ||
                'Error al cargar clientes',
              { status: 'error' }
            )
            .subscribe();
          return of([] as Client[]);
        })
      )
    })
      .pipe(
        switchMap(({ zones, clients }) =>
          this.permissionsService.isOkMobility().pipe(
            take(1),
            switchMap((isOkMobility) =>
              isOkMobility
                ? this.permissionsService.getOkMobilityOfficeToken().pipe(
                    take(1),
                    map((officeToken) => ({
                      zones,
                      clients,
                      isOkMobility,
                      officeToken
                    }))
                  )
                : of({
                    zones,
                    clients,
                    isOkMobility,
                    officeToken: null as string | null
                  })
            )
          )
        )
      )
      .subscribe(({ zones, clients, isOkMobility, officeToken }) => {
        let filteredZones = zones;
        let filteredClients = clients;

        if (isOkMobility) {
          filteredZones = zones.filter((z) => this.isSpanishZone(z));

          const normalizedOfficeToken = this.normalizeFilterText(officeToken);
          const officeMatchesSomeZone = normalizedOfficeToken
            ? filteredZones.some((z) =>
                this.normalizeFilterText(z.zoneName).includes(
                  normalizedOfficeToken
                )
              )
            : false;

          if (normalizedOfficeToken && officeMatchesSomeZone) {
            filteredZones = filteredZones.filter((z) =>
              this.normalizeFilterText(z.zoneName).includes(
                normalizedOfficeToken
              )
            );
            filteredClients = clients.filter((c) =>
              this.normalizeFilterText(c.businessName).includes(
                normalizedOfficeToken
              )
            );
          }
        }

        this.zones = filteredZones;
        this.clients = filteredClients;
        this.zonesLoaded = false;
        this.clientsLoaded = false;

        if (this.service_info && this.type_info !== ModalMode.Add) {
          this.patchFromService(this.service_info);
        }
      });
  }

  private isSpanishZone(zone: Zone): boolean {
    const tz = (zone.timeZone ?? '').toLowerCase();
    return tz === 'europe/madrid' || tz === 'atlantic/canary';
  }

  private normalizeFilterText(value: string | null | undefined): string {
    return (value ?? '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9\s-]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  private buildForm(): void {
    this.createFormControls();
    this.setupTimeValidators();
    this.setupConditionalEnabling();
    this.subscribeToServiceOptionChecks();
  }

  private getCurrencyByZone(zoneId: string): void {
    if (zoneId) {
      this.currency = getCurrencySymbolByZoneId(zoneId);
    } else {
      this.currency = this.isCopec ? 'CLP' : '€';
    }
    try {
      this.cd.detectChanges();
    } catch {}
  }
  private createFormControls(): void {
    const timePattern = /^([01]\d|2[0-3]):([0-5]\d)$/;

    this.form = this.fb.group(
      {
        zone: this.fb.control<Zone | null>(null, {
          validators: [Validators.required],
          updateOn: 'change'
        }),
        clientName: this.fb.control<Client | null>(null, {
          validators: [Validators.required],
          updateOn: 'change'
        }),

        // ----- FECHA / HORA -----
        serviceStartDate: this.fb.control<string>('', {
          validators: [Validators.required],
          updateOn: 'change'
        }),
        singleTime: this.fb.control<HourType | null>(null, {
          validators: [Validators.required],
          updateOn: 'change'
        }),
        serviceStartHour: this.fb.control<string>('', {
          updateOn: 'change',
          validators: [Validators.pattern(TIME_24H_REGEX)]
        }),
        workdayStartTime: this.fb.control<string>('', {
          updateOn: 'change',
          validators: [Validators.pattern(TIME_24H_REGEX)]
        }),
        workdayEndTime: this.fb.control<string>('', {
          updateOn: 'change',
          validators: [Validators.pattern(TIME_24H_REGEX)]
        }),

        // ----- VEHÍCULO -----
        vehicleTypeName: this.fb.control<VehicleChassisTypeEnum | null>(null, {
          validators: [Validators.required],
          updateOn: 'change'
        }),
        vehicleBrand: this.fb.control<string>('', {
          validators: [Validators.maxLength(30)],
          updateOn: 'change'
        }),
        vehicleModel: this.fb.control<string>('', {
          validators: [Validators.maxLength(30)],
          updateOn: 'change'
        }),
        licensePlate: this.fb.control<string>('', {
          validators: [Validators.minLength(2), Validators.maxLength(30)],
          updateOn: 'change'
        }),
        vehicleFuelName: this.fb.control<VehicleFuelTypeEnum | null>(null, {
          validators: [Validators.required],
          updateOn: 'change'
        }),

        // ----- DIRECCIONES -----
        originAddress: this.fb.control<string>('', {
          validators: [Validators.required],
          updateOn: 'blur'
        }),
        originAddressPlaceId: ['', Validators.required], // Place ID para Google Maps
        originAddressDetails: this.fb.control<string>('', {
          validators: [Validators.maxLength(100)],
          updateOn: 'blur'
        }),
        originAddressGeopointLt: [0],
        originAddressGeopointLn: [0],
        destinationAddress: this.fb.control<string>('', {
          validators: [Validators.required],
          updateOn: 'blur'
        }),
        destinationAddressPlaceId: ['', Validators.required], // Place ID para Google Maps
        destinationAddressDetails: this.fb.control<string>('', {
          validators: [Validators.maxLength(100)],
          updateOn: 'blur'
        }),
        destinationAddressGeopointLt: [0],
        destinationAddressGeopointLn: [0],
        sameOriginAddress: this.fb.control<boolean>(false, {
          updateOn: 'change'
        }),

        providerAddress: [''],
        providerAddressGeopointLt: [0],
        providerAddressGeopointLn: [0],
        tyresAddress: [''],
        tyresAddressGeopointLt: [0],
        tyresAddressGeopointLn: [0],

        // ----- CONTACTOS -----
        isOriginContactExternal: this.fb.control<boolean>(false, {
          updateOn: 'change'
        }),
        originContactName: this.fb.control<string>('', {
          validators: [
            Validators.required,
            Validators.minLength(2),
            Validators.maxLength(50)
          ],
          updateOn: 'change'
        }),

        originalContactPhoneNumberPhoneIso: [TuiCountryIsoCode.ES],
        originContactPhoneNumber: this.fb.control<string>('', {
          validators: [
            Validators.required,
            phoneValidatorV2('originalContactPhoneNumberPhoneIso')
          ],
          updateOn: 'change'
        }),
        originContactEmailAddress: this.fb.control<string>('', {
          validators: [Validators.email, strictEmailValidator],
          updateOn: 'change'
        }),
        originContactDocumentType: this.fb.control(null, {
          validators: [],
          updateOn: 'change'
        }),
        originContactDocumentNumber: this.fb.control('', {
          validators: [],
          updateOn: 'change'
        }),
        sameContactOriginDestiny: this.fb.control<boolean>(false, {
          updateOn: 'change'
        }),
        isDestinationContactExternal: this.fb.control<boolean>(false, {
          updateOn: 'change'
        }),
        destinationContactName: this.fb.control<string>('', {
          validators: [
            Validators.required,
            Validators.minLength(2),
            Validators.maxLength(50)
          ],
          updateOn: 'change'
        }),

        destinationContactPhoneIso: [TuiCountryIsoCode.ES],
        destinationContactPhoneNumber: this.fb.control<string>('', {
          validators: [
            Validators.required,
            phoneValidatorV2('destinationContactPhoneIso')
          ],
          updateOn: 'change'
        }),
        destinationContactEmailAddress: this.fb.control<string>('', {
          validators: [Validators.email, strictEmailValidator],
          updateOn: 'change'
        }),
        destinationContactDocumentType: this.fb.control(null, {
          validators: [],
          updateOn: 'change'
        }),
        destinationContactDocumentNumber: this.fb.control('', {
          validators: [],
          updateOn: 'change'
        }),

        // ----- SERVICIO -----
        vertical: this.fb.control<string | null>(
          { value: null, disabled: true },
          {
            validators: [Validators.required],
            updateOn: 'change'
          }
        ),
        product: this.fb.control<string | null>(
          { value: null, disabled: true },
          {
            validators: [Validators.required],
            updateOn: 'change'
          }
        ),
        provider: this.fb.control<string | null>(
          { value: null, disabled: true },
          { updateOn: 'change' }
        ),

        comments: this.fb.control<string>('', {
          validators: [Validators.maxLength(500)],
          updateOn: 'blur'
        }),

        orderType: [1],
        aditionalTyresInfo: [false],
        vehicleFuelType: [''],
        monetaryAmount: [null],
        vehicleStatus: [''],
        appointmentCode: [''],
        isInspectionPaid: [false],
        appointmentDate: [''],
        appointmentHour: this.fb.control<string>('', {
          updateOn: 'blur',
          validators: [Validators.pattern(TIME_24H_REGEX)]
        }),

        extras: this.fb.array([]),

        tyreOrderType: this.fb.control<'4iguales' | '2x2' | 'custom' | null>(
          null,
          Validators.required
        ),
        tyreSeason: this.fb.control<
          'verano' | 'invierno' | '4estaciones' | null
        >(null, Validators.required),
        withStuds: [false],
        tyreWidth: ['', [Validators.required, Validators.pattern(TYRE_INTEGER_PATTERN)]],
        tyreSeries: ['', [Validators.required, Validators.pattern(TYRE_INTEGER_PATTERN)]],
        tyreDiameter: ['', [Validators.required, Validators.pattern(TYRE_INTEGER_PATTERN)]],
        tyreLoadIndex: ['', [Validators.required, Validators.pattern(TYRE_INTEGER_PATTERN)]],
        tyreSpeedIndex: ['', [Validators.required, Validators.pattern(TYRE_SPEED_INDEX_PATTERN)]],
        tyreBrand: ['', Validators.required],
        tyreModel: ['', Validators.required],
        tyreCount: [
          1,
          [Validators.required, Validators.min(1), Validators.pattern(TYRE_INTEGER_PATTERN)]
        ],
        tyreUnitCost: [
          '',
          [Validators.required, Validators.min(0), Validators.pattern(TYRE_DECIMAL_PATTERN)]
        ],
        serviceOption: this.fb.control<ServiceOptionType | null>(
          null,
          Validators.required
        ),
        exchangeOld: [false],
        needAlignment: [false],
        deliveryNoteId: this.fb.control<string>('', {
          updateOn: 'change'
        }),

        customProductPrice: this.fb.control<number | null>(null, {
          updateOn: 'change'
        }),
        customCaflerFee: this.fb.control<number | null>(null, {
          updateOn: 'change'
        }),
        addFixedFee: this.fb.control<boolean>(false, {
          updateOn: 'change'
        }),
        addUrgencyFee: this.fb.control<boolean>(false, {
          updateOn: 'change'
        }),
        isPoolService: this.fb.control<boolean>(false, {
          updateOn: 'change'
        }),
        customPoolPrice: this.fb.control<number | null>(
          { value: null, disabled: true },
          { updateOn: 'change' }
        )
      },
      {
        validators: [this.timeRangeValidator()]
      }
    );
  }

  openPhoneContainer(clickEvent: MouseEvent): void {
    const clickedElement = clickEvent.currentTarget as HTMLElement;

    if (clickedElement === this.openElement) {
      this.openElement = null;
    }

    if (clickedElement !== this.openElement) {
      const dropdownElements: HTMLCollectionOf<HTMLElement> =
        document.getElementsByTagName(
          'tui-dropdown'
        ) as HTMLCollectionOf<HTMLElement>;

      if (dropdownElements.length > 0 && dropdownElements[0] !== undefined) {
        const foundDropdownElement: HTMLElement = dropdownElements[0];
        const targetContainer: HTMLElement | null = document.getElementById(
          'modal_show_countries_phones'
        );

        if (targetContainer) {
          targetContainer.innerHTML = '';
          targetContainer.append(foundDropdownElement);
        } else {
          console.warn(
            "El contenedor 'modal_show_countries_phones' no fue encontrado en el DOM."
          );
        }
      } else {
        console.warn("No se encontraron elementos 'tui-dropdown' en el DOM.");
      }
      this.openElement = clickedElement;
    } else {
      this.openElement = null;
    }
  }

  addCustomTyre(): void {
    this.tyresArray.push(
      this.fb.group({
        tyreSeason: [null, Validators.required],
        withStuds: [false],
        exchangeOld: [false],
        tyreWidth: ['', [Validators.required, Validators.pattern(TYRE_INTEGER_PATTERN)]],
        tyreSeries: ['', [Validators.required, Validators.pattern(TYRE_INTEGER_PATTERN)]],
        tyreDiameter: ['', [Validators.required, Validators.pattern(TYRE_INTEGER_PATTERN)]],
        tyreLoadIndex: ['', [Validators.required, Validators.pattern(TYRE_INTEGER_PATTERN)]],
        tyreSpeedIndex: ['', [Validators.required, Validators.pattern(TYRE_SPEED_INDEX_PATTERN)]],
        tyreBrand: ['', Validators.required],
        tyreModel: ['', Validators.required],
        tyreCount: [
          null,
          [Validators.required, Validators.min(1), Validators.pattern(TYRE_INTEGER_PATTERN)]
        ],
        tyreUnitCost: [
          null,
          [Validators.required, Validators.min(0), Validators.pattern(TYRE_DECIMAL_PATTERN)]
        ],
        needAlignment: [false]
      })
    );
  }

  private subscribeToServiceOptionChecks(): void {
    const serviceOptionCtrl = this.form.get('serviceOption');
    const exchangeOldCtrl = this.form.get('exchangeOld');
    const needAlignmentCtrl = this.form.get('needAlignment');

    if (!serviceOptionCtrl || !exchangeOldCtrl || !needAlignmentCtrl) return;

    serviceOptionCtrl.valueChanges.subscribe((option) => {
      exchangeOldCtrl.reset(false, { emitEvent: false });
      needAlignmentCtrl.reset(false, { emitEvent: false });

      switch (option) {
        case ServiceOptionType.SinMovilidad:
        case ServiceOptionType.Movilidad:
          exchangeOldCtrl.enable({ emitEvent: false });
          needAlignmentCtrl.enable({ emitEvent: false });
          break;

        case ServiceOptionType.MontajeDomicilio:
          exchangeOldCtrl.enable({ emitEvent: false });
          needAlignmentCtrl.disable({ emitEvent: false });
          break;

        case ServiceOptionType.Envio:
        default:
          exchangeOldCtrl.disable({ emitEvent: false });
          needAlignmentCtrl.disable({ emitEvent: false });
          break;
      }
    });
  }

  private setupConditionalValidations(): void {
    /* Helpers */
    const isTyres = () => this.activeVerticalValue === this.TYRESVERTICAL;
    const isRefueling = () =>
      this.activeVerticalValue === this.REFUELINGVERTICAL;
    const isCustomTyre = () =>
      isTyres() && this.form.get('tyreOrderType')!.value === 'custom';

    interface ConditionalValidation {
      controlName: string;
      validator: ValidatorFn;
      predicate: () => boolean;
    }

    const tyreFields = [
      'tyreSeason',
      'tyreWidth',
      'tyreSeries',
      'tyreDiameter',
      'tyreLoadIndex',
      'tyreSpeedIndex',
      'tyreBrand',
      'tyreModel',
      'tyreCount',
      'tyreUnitCost',
      'serviceOption'
    ];

    const conditions: ConditionalValidation[] = [
      ...tyreFields.map((name) => ({
        controlName: name,
        validator: Validators.required,
        predicate: () => isTyres() && !isCustomTyre()
      })),
      {
        controlName: 'tyreOrderType',
        validator: Validators.required,
        predicate: isTyres
      },

      {
        controlName: 'monetaryAmount',
        validator: Validators.required,
        predicate: isRefueling
      }
    ];

    const trigger$ = merge(
      this.form.get('vertical')!.valueChanges,
      this.form.get('tyreOrderType')!.valueChanges,
      this.destroy$
    ).pipe(startWith(null));

    trigger$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      for (const { controlName, validator, predicate } of conditions) {
        const ctrl = this.form.get(controlName);
        if (!ctrl) continue;
        if (predicate()) {
          ctrl.addValidators(validator);
        } else {
          ctrl.removeValidators(validator);
        }
        ctrl.updateValueAndValidity({ emitEvent: false });
      }
    });
  }

  removeCustomTyre(i: number): void {
    this.tyresArray.removeAt(i);
  }

  private setupTimeValidators(): void {
    this.form
      .get('singleTime')!
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((mode: HourType) => {
        const hCtrl = this.form.get('serviceStartHour')!;
        const sCtrl = this.form.get('workdayStartTime')!;
        const eCtrl = this.form.get('workdayEndTime')!;

        [hCtrl, sCtrl, eCtrl].forEach((c) => c.clearValidators());
        if (mode === 'Hora fija') {
          hCtrl.setValidators([
            Validators.required,
            Validators.pattern(TIME_24H_REGEX)
          ]);
        } else if (mode === 'Franja horaria') {
          sCtrl.setValidators([
            Validators.required,
            Validators.pattern(TIME_24H_REGEX)
          ]);
          eCtrl.setValidators([
            Validators.required,
            Validators.pattern(TIME_24H_REGEX)
          ]);
        }
        [hCtrl, sCtrl, eCtrl].forEach((c) => c.updateValueAndValidity());
      });
  }

  onTimeInput(
    controlName:
      | 'serviceStartHour'
      | 'workdayStartTime'
      | 'workdayEndTime'
      | 'appointmentHour'
  ): void {
    const control = this.form.get(controlName) as AbstractControl<
      string | null
    >;
    if (!control) return;

    const raw = (control.value ?? '').replace(/\D/g, '');
    if (raw.length < 3) {
      control.setValue(raw, { emitEvent: false });
      return;
    }

    const formatted = `${raw.slice(0, 2)}:${raw.slice(2, 4)}`;
    if (TIME_24H_REGEX.test(formatted)) {
      control.setValue(formatted, { emitEvent: false });
    } else if (raw.length < 5) {
      control.setValue(raw, { emitEvent: false });
    } else {
      control.setValue('', { emitEvent: false });
    }
  }

  private setupConditionalEnabling(): void {
    const zoneCtrl = this.form.get('zone')!;
    const clientCtrl = this.form.get('clientName')!;
    const vehicleTypeCtrl = this.form.get('vehicleTypeName')!;
    const vehicleFuelCtrl = this.form.get('vehicleFuelName')!;
    const verticalCtrl = this.form.get('vertical')!;
    const productCtrl = this.form.get('product')!;
    const providerCtrl = this.form.get('provider')!;

    const updateProviderState = () => {
      if (zoneCtrl.value && clientCtrl.value && verticalCtrl.value) {
        providerCtrl.enable({ emitEvent: false });
      } else {
        providerCtrl.reset(null, { emitEvent: false });
        providerCtrl.disable({ emitEvent: false });
      }
    };

    if (this.type_info === ModalMode.Edit) {
      verticalCtrl.disable({ emitEvent: false });
      productCtrl.disable({ emitEvent: false });

      const customProductPriceCtrl = this.form.get('customProductPrice');
      const customCaflerFeeCtrl = this.form.get('customCaflerFee');
      const addFixedFeeCtrl = this.form.get('addFixedFee');
      const addUrgencyFeeCtrl = this.form.get('addUrgencyFee');
      const isPoolServiceCtrl = this.form.get('isPoolService');
      const customPoolPriceCtrl = this.form.get('customPoolPrice');

      if (customProductPriceCtrl) customProductPriceCtrl.disable({ emitEvent: false });
      if (customCaflerFeeCtrl) customCaflerFeeCtrl.disable({ emitEvent: false });
      if (addFixedFeeCtrl) addFixedFeeCtrl.disable({ emitEvent: false });
      if (addUrgencyFeeCtrl) addUrgencyFeeCtrl.disable({ emitEvent: false });
      if (isPoolServiceCtrl) isPoolServiceCtrl.disable({ emitEvent: false });
      if (customPoolPriceCtrl) customPoolPriceCtrl.disable({ emitEvent: false });

      zoneCtrl.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe(updateProviderState);
      clientCtrl.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe(updateProviderState);
      verticalCtrl.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe(updateProviderState);

      updateProviderState();
      return;
    }

    const updateVerticalState = () => {
      if (
        zoneCtrl.value &&
        clientCtrl.value &&
        vehicleTypeCtrl.value != null &&
        vehicleFuelCtrl.value != null
      ) {
        verticalCtrl.enable({ emitEvent: false });
      } else {
        verticalCtrl.reset(null, { emitEvent: false });
        verticalCtrl.disable({ emitEvent: false });
      }
    };

    const updateProductState = () => {
      if (zoneCtrl.value && clientCtrl.value && verticalCtrl.value) {
        productCtrl.enable({ emitEvent: false });
      } else {
        productCtrl.reset(null, { emitEvent: false });
        productCtrl.disable({ emitEvent: false });
      }
    };

    const baseFilterChanged = () => {
      verticalCtrl.reset(null, { emitEvent: false });
      productCtrl.reset(null, { emitEvent: false });

      this.form.patchValue(
        {
          appointmentCode: '',
          appointmentDate: '',
          appointmentHour: '',
          isInspectionPaid: false,
          vehicleStatus: ''
        },
        { emitEvent: false }
      );

      this.form.patchValue(
        {
          monetaryAmount: null,
          vehicleFuelType: ''
        },
        { emitEvent: false }
      );

      this.clearTyreSection();

      this.activeVertical = null;
      this.activeVerticalValue = null;
      this.activeProductValue = null;

      this.clearProviders();
      this.reloadProducts();

      updateVerticalState();
      updateProductState();
      updateProviderState();
    };

    zoneCtrl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(baseFilterChanged);
    clientCtrl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(baseFilterChanged);
    vehicleTypeCtrl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(baseFilterChanged);
    vehicleFuelCtrl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(baseFilterChanged);

    verticalCtrl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      updateProductState();
      updateProviderState();
    });

    updateVerticalState();
    updateProductState();
    updateProviderState();
  }

  get tyresArray(): FormArray<FormGroup> {
    return this.form.get('extras') as FormArray<FormGroup>;
  }

  get additionalCostAmount(): string {
    let total = 0;

    const sumCosts = (needAlignment: boolean, exchangeOld: boolean) => {
      if (needAlignment) total += ALIGNMENT_COST;
      if (exchangeOld) total += EXCHANGE_OLD_COST;
    };

    if (this.tyresArray && this.tyresArray.length > 0) {
      this.tyresArray.controls.forEach((group) => {
        sumCosts(
          !!group.get('needAlignment')?.value,
          !!group.get('exchangeOld')?.value
        );
      });
    } else {
      sumCosts(
        !!this.form.get('needAlignment')?.value,
        !!this.form.get('exchangeOld')?.value
      );
    }

    return `${total.toLocaleString('es-CL')} CLP`;
  }

  private timeRangeValidator(): ValidatorFn {
    return (group: AbstractControl): ValidationErrors | null => {
      const mode = group.get('singleTime')?.value as HourType;
      if (mode !== 'Franja horaria') {
        return null;
      }

      const start = group.get('workdayStartTime')?.value as string;
      const end = group.get('workdayEndTime')?.value as string;

      if (!start || !end) {
        return {
          missingRange: this.translate.instant(
            'modals.add_service.missing_range'
          )
        };
      }

      // 2) start >= end → invalidRange
      if (start >= end) {
        return {
          invalidRange: this.translate.instant(
            'modals.add_service.invalid_range'
          )
        };
      }

      // 3) todo OK
      return null;
    };
  }

  getVerticalToTraduction(realName: string): string {
    return (
      this.verticalsTypes.find((v) => v.realName === realName)?.name ?? realName
    );
  }

  private listenFormChanges(): void {
    this.uppercaseLicensePlate();
    this.onZoneChange();
    this.onVerticalChange();
    this.onProductChange();
    this.onWorkdayStartChange();
    this.onSameAddressToggle();
    this.onSameContactToggle();
    this.setupConditionalValidations();
    this.onFeeToggleChanges();
  }

  private onFeeToggleChanges(): void {
    this.form
      .get('isPoolService')!
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((enabled: boolean) => {
        const ctrl = this.form.get('customPoolPrice')!;
        if (enabled) {
          ctrl.enable({ emitEvent: false });
        } else {
          ctrl.reset(null, { emitEvent: false });
          ctrl.disable({ emitEvent: false });
        }
      });
  }

  get marginAmount(): number {
    const productPrice = Number(this.form.get('customProductPrice')?.value ?? 0);
    const poolPrice = Number(this.form.get('customPoolPrice')?.value ?? 0);
    return productPrice - poolPrice;
  }

  get marginPercentage(): number {
    const productPrice = Number(this.form.get('customProductPrice')?.value ?? 0);
    if (productPrice === 0) return 0;
    return (this.marginAmount / productPrice) * 100;
  }

  private onZoneChange(): void {
    this.form
      .get('zone')!
      .valueChanges.pipe(
        debounceTime(300),
        distinctUntilChanged((a: Zone, b: Zone) => a?.zoneId === b?.zoneId),
        tap(() => {
          this.resetProviderAndProduct();
          this.reloadProducts();
        }),
        filter((zone: Zone | null): zone is Zone => !!zone),
        tap((zone: Zone) => {
          this.syncContactPhoneIsoByZone(zone);
        }),
        tap((zone: Zone) => {
          this.getCurrencyByZone(zone.zoneId);
        }),
        tap(() => {
          const vertical = this.form.get('vertical')!.value;
          const product = this.form.get('product')!.value;
          if (vertical && product) {
            this.updateProviderAndGarage(product);
          }
        }),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  private syncContactPhoneIsoByZone(zone: Zone): void {
    const iso = this.getPhoneIsoFromZone(zone);

    this.form.patchValue(
      {
        originalContactPhoneNumberPhoneIso: iso,
        destinationContactPhoneIso: iso
      },
      { emitEvent: false }
    );

    this.form
      .get('originContactPhoneNumber')
      ?.updateValueAndValidity({ emitEvent: false });
    this.form
      .get('destinationContactPhoneNumber')
      ?.updateValueAndValidity({ emitEvent: false });
  }

  private getPhoneIsoFromZone(zone: Zone): TuiCountryIsoCode {
    const zoneId = (zone.zoneId ?? '').toLowerCase();
    const zoneName = this.normalizeFilterText(zone.zoneName);
    const zoneTimeZone = (zone.timeZone ?? '').toLowerCase();

    const ukZoneIds = new Set([
      ZoneIdType.LONDON.toLowerCase(),
      ZoneIdType.OUT_OPERATIVE_UK_AREA.toLowerCase()
    ]);

    const franceZoneIds = new Set([
      ZoneIdType.OUT_OPERATIVE_FRANCE_AREA.toLowerCase(),
      ZoneIdType.PARIS.toLowerCase(),
      ZoneIdType.LYON.toLowerCase()
    ]);

    const chileZoneIds = new Set([
      ZoneIdType.CHILE.toLowerCase()
    ]);

    const spainZoneIds = new Set([
      ZoneIdType.MADRID.toLowerCase(),
      ZoneIdType.SEVILLA.toLowerCase(),
      ZoneIdType.BARCELONA.toLowerCase(),
      ZoneIdType.BILBAO.toLowerCase(),
      ZoneIdType.ALICANTE.toLowerCase(),
      ZoneIdType.BALEARES.toLowerCase(),
      ZoneIdType.ZARAGOZA.toLowerCase(),
      ZoneIdType.VALENCIA.toLowerCase(),
      ZoneIdType.OUT_OPERATIVE_SPAIN_AREA.toLowerCase(),
      ZoneIdType.VIGO.toLowerCase(),
      ZoneIdType.OVIEDO.toLowerCase(),
      ZoneIdType.SANTANDER.toLowerCase(),
      ZoneIdType.MALAGA.toLowerCase()
    ]);

    if (chileZoneIds.has(zoneId) || zoneTimeZone.includes('santiago') || zoneName.includes('chile')) {
      return TuiCountryIsoCode.CL;
    }

    if (
      ukZoneIds.has(zoneId) ||
      zoneTimeZone.includes('london') ||
      zoneName.includes('london') ||
      zoneName.includes('uk') ||
      zoneName.includes('reino unido')
    ) {
      return TuiCountryIsoCode.GB;
    }

    if (
      franceZoneIds.has(zoneId) ||
      zoneTimeZone.includes('paris') ||
      zoneName.includes('france') ||
      zoneName.includes('francia')
    ) {
      return TuiCountryIsoCode.FR;
    }

    if (spainZoneIds.has(zoneId)) {
      return TuiCountryIsoCode.ES;
    }

    return TuiCountryIsoCode.ES;
  }

  private resetProviderAndProduct(): void {
    this.form.patchValue(
      { provider: null, product: null },
      { emitEvent: false }
    );

    this.clearProviders();
    this.revertContactExternalControls();
  }

  // ── 2. VERTICAL → limpiar + recargar productos ────────────────────────────
  private onVerticalChange(): void {
    this.form
      .get('vertical')!
      .valueChanges.pipe(
        tap((realName) => {
          this.resetProviderAndProduct();
          this.activeVertical = realName ?? '';
          this.activeVerticalValue =
            this.mapVerticalValue(realName ?? null) ?? null;
        }),

        tap(() => {
          const isTyres =
            this.mapVerticalValue(
              this.form.get('vertical')!.value as string
            ) === this.TYRESVERTICAL;
          const extrasCtrl = this.form.get('extras') as FormArray<FormGroup>;
          if (isTyres) {
            extrasCtrl.enable({ emitEvent: false });
          } else {
            this.clearTyreSection();
          }

          const tyreOrderTypeCtrl = this.form.get('tyreOrderType');
          const serviceOptionCtrl = this.form.get('serviceOption');
          tyreOrderTypeCtrl?.updateValueAndValidity({ emitEvent: false });
          serviceOptionCtrl?.updateValueAndValidity({ emitEvent: false });
        }),

        tap(() => this.reloadProducts()),

        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  private onProductChange(): void {
    this.form
      .get('product')!
      .valueChanges.pipe(
        distinctUntilChanged(),

        // 1) Actualizar meta‑info del producto y limpiar proveedores
        tap((name) => {
          const p = this.productsOfVertical.find(
            (prod) => prod.traduction === name
          );
          this.activeProductValue = p ? Number(p.verticalType) : null;
          this.clearProviders();
          this.enforceTransferContactRules(p ?? null);
        }),

        tap(() => {
          const isTyres =
            this.mapVerticalValue(
              this.form.get('vertical')!.value as string
            ) === this.TYRESVERTICAL;

          const extrasCtrl = this.form.get('extras') as FormArray<FormGroup>;
          if (isTyres) {
            extrasCtrl.enable({ emitEvent: false });
          } else {
            this.clearTyreSection();
          }
        }),

        tap((name) => this.updateProviderAndGarage(name as string)),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  private clearProviders(): void {
    this.providersLoading = false;
    this.showProvider = false;
    this.providers = [];
    this.providersName = [];
    const ctrl = this.form.get('provider')!;
    ctrl.reset({ value: null, disabled: true });
  }

  private updateProviderAndGarage(productName: string | null): void {
    const zone: Zone | null = this.form.get('zone')!.value;
    const client: Client | null = this.form.get('clientName')!.value;
    const providerCtrl = this.form.get('provider')!;

    if (!zone || !client || !productName) {
      this.clearProviders();
      this.showGarageAddr = false;
      return;
    }

    // Buscamos los datos del producto
    const pData = this.productsOfVertical.find(
      (p) => p.traduction === productName
    );

    // Detectar repostaje para mostrar proveedores siempre
    const isRepostaje = this.form.get('vertical')!.value === 'Repostaje';

    // Si el producto no existe o no requiere proveedor (y no es repostaje) → limpiamos y salimos
    if ((!pData?.doesRequiresProvider && !isRepostaje) || !pData) {
      this.clearProviders();
    } else {
      this.showProvider = true;
      this.providersLoading = true;

      // Obtenemos proveedores de la API y filtramos por productId
      this.hs
        .getProviders(zone.zoneId)
        .pipe(
          map((resp: GetServiceDetailProviderResponse) =>
            // Si es repostaje, dejamos todos; si no, filtramos por productId
            isRepostaje
              ? resp.providersInZone
              : resp.providersInZone.filter((pr) =>
                  pr.productIds.includes(pData.productId)
                )
          ),
          catchError((err) => {
            console.error('Error cargando proveedores:', err);
            this.alerts
              .open(
                this.translate.instant('modals.add_service.load_providers') ||
                  'Error al cargar proveedores',
                { status: 'error' }
              )
              .subscribe();
            return of([] as ProviderServiceDetail[]);
          }),
          finalize(() => {
            this.providersLoading = false;
          })
        )
        .subscribe((filtered) => {
          this.providers = filtered;
          this.providersName = filtered.map((pr) => pr.name);

          if (filtered.length > 0) {
            this.showProvider = true;
            providerCtrl.enable({ emitEvent: false });
          } else {
            this.showProvider = false;

            // Si no hay ninguno válido, volvemos a limpiar
            this.clearProviders();
          }

          this.cd.detectChanges();
        });
    }

    // Mostrar dirección de taller si aplica
    this.showGarageAddr = Boolean(
      pData?.productLocalizationCode?.includes('360')
    );
  }

  private onWorkdayStartChange(): void {
    this.form
      .get('workdayStartTime')!
      .valueChanges.pipe(
        filter(Boolean),
        distinctUntilChanged(),
        tap((time) => this.calculateTimePeriods(time)),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  private calculateTimePeriods(time: string): void {
    const hh = Number(time.split(':')[0]);
    this.hours2 = tuiCreateTimePeriods(
      hh + 1,
      24,
      Array.from({ length: 60 }, (_, i) => i)
    );
  }

  private onSameAddressToggle(): void {
    this.form
      .get('sameOriginAddress')!
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((same) => {
        const addr = this.form.get('destinationAddress')!;
        const detl = this.form.get('destinationAddressDetails')!;

        const isReadOnlyMode =
          this.type_info === ModalMode.Edit ||
          this.type_info === ModalMode.Duplicate;

        if (isReadOnlyMode) {
          if (same) {
            addr.disable({ emitEvent: false });
            detl.disable({ emitEvent: false });
          } else {
            addr.enable({ emitEvent: false });
            detl.enable({ emitEvent: false });
          }
        } else {
          if (same) {
            this.copyOriginToDestination();
            addr.disable({ emitEvent: false });
            detl.disable({ emitEvent: false });
          } else {
            addr.enable({ emitEvent: false });
            detl.enable({ emitEvent: false });
            addr.reset('');
            detl.reset('');
          }
        }

        this.cd.detectChanges();
      });
  }

  private onSameContactToggle(): void {
    const sameCtrl = this.form.get('sameContactOriginDestiny')!;
    const originKeys: Array<keyof ServiceForm> = [
      'originContactName',
      'originContactPhoneNumber',
      'originContactEmailAddress',
      'isOriginContactExternal',
      'originalContactPhoneNumberPhoneIso',
      'originContactDocumentType',
      'originContactDocumentNumber'
    ];
    const destKeys: Array<keyof ServiceForm> = [
      'destinationContactName',
      'destinationContactPhoneNumber',
      'destinationContactEmailAddress',
      'isDestinationContactExternal',
      'destinationContactDocumentType',
      'destinationContactDocumentNumber'
    ];

    sameCtrl.valueChanges
      .pipe(
        startWith(sameCtrl.value),
        tap((same) => {
          const transferRole = this.getTransferProductRole(); // 'collect' | 'return' | null

          if (same) {
            this.copyOriginContactToDest();
            destKeys.forEach((k) => {
              this.form.get(k)!.disable({ emitEvent: false });
            });
            const docFields = [
              'destinationContactDocumentType',
              'destinationContactDocumentNumber'
            ];
            if (same) {
              destKeys.forEach((k) => {
                this.form.get(k)!.disable({ emitEvent: false });
              });
              docFields.forEach((k) => {
                this.form.get(k)!.disable({ emitEvent: false });
              });
            } else {
              destKeys.forEach((k) => {
                const ctrl = this.form.get(k)!;
                if (
                  k === 'isDestinationContactExternal' &&
                  transferRole !== null
                ) {
                  ctrl.disable({ emitEvent: false });
                } else {
                  ctrl.enable({ emitEvent: false });
                  ctrl.markAsUntouched();
                }
              });
              docFields.forEach((k) => {
                this.form.get(k)!.enable({ emitEvent: false });
              });
            }
          } else {
            destKeys.forEach((k) => {
              const ctrl = this.form.get(k)!;

              if (
                k === 'isDestinationContactExternal' &&
                transferRole !== null
              ) {
                ctrl.disable({ emitEvent: false });
              } else {
                ctrl.enable({ emitEvent: false });
                ctrl.markAsUntouched();
              }
            });
          }

          try {
            this.cd.detectChanges();
          } catch {}
        }),

        switchMap((same) =>
          same
            ? merge(
                ...originKeys.map((k) =>
                  this.form
                    .get(k)!
                    .valueChanges.pipe(startWith(this.form.get(k)!.value))
                )
              ).pipe(delay(0))
            : EMPTY
        ),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.copyOriginContactToDest();
      });
  }

  private initAutocomplete(key: AutoKey): void {
    if (!window.google?.maps?.places) return;

    const refMap: Record<AutoKey, ElementRef<HTMLInputElement>> = {
      origin: this.originField,
      destination: this.destinationField,
      tyres: this.tyresField,
      provider: this.providerField
    };

    const addrKeyMap: Record<AutoKey, keyof ServiceForm> = {
      origin: 'originAddress',
      destination: 'destinationAddress',
      tyres: 'tyresAddress',
      provider: 'providerAddress'
    };

    const ref = refMap[key];
    if (!ref) return;

    const ac = new google.maps.places.Autocomplete(ref.nativeElement, {
      fields: [
        'address_components',
        'formatted_address',
        'geometry',
        'name',
        'place_id'
      ]
    });

    const listener = ac.addListener('place_changed', () =>
      this.ngZone.run(() => {
        const place = ac.getPlace();
        if (!place.geometry || !place.place_id) return;

        const address =
          place.name && place.formatted_address
            ? `${place.name}, ${place.formatted_address}`
            : (place.formatted_address ?? '');

        const lat = place.geometry.location.lat();
        const lng = place.geometry.location.lng();
        const addrKey = addrKeyMap[key];
        const placeIdKey = `${addrKey}PlaceId` as keyof ServiceForm;

        const patch: Partial<ServiceForm> = {
          [addrKey]: address,
          [placeIdKey]: place.place_id,
          [`${addrKey}GeopointLt`]: lat,
          [`${addrKey}GeopointLn`]: lng
        };

        this.form.patchValue(patch, { emitEvent: false });

        if (key === 'origin' && this.form.value.sameOriginAddress) {
          this.copyOriginToDestination();
        }
      })
    );

    this.autocompleteListeners.push(listener);
  }

  clearPlaceId(key: 'origin' | 'destination'): void {
    const placeField =
      key === 'origin' ? 'originAddressPlaceId' : 'destinationAddressPlaceId';
    const latField =
      key === 'origin'
        ? 'originAddressGeopointLt'
        : 'destinationAddressGeopointLt';
    const lngField =
      key === 'origin'
        ? 'originAddressGeopointLn'
        : 'destinationAddressGeopointLn';

    this.form.patchValue(
      {
        [placeField]: '',
        [latField]: 0,
        [lngField]: 0
      } as any,
      { emitEvent: false }
    );
  }

  private resolveVerticalReal(detail: ServiceDetail): string {
    return (
      this.verticalsTypes.find((v) => v.value === detail.verticalType)
        ?.realName ?? ''
    );
  }

  private patchFromService(detail: ServiceDetail): void {
    if (!this.ensureDetailPresent(detail)) {
      return;
    }

    if (!this.patchZoneAndOptionalClient(detail)) {
      return;
    }

    const zone = this.findZone(detail.zoneId)!;

    const { start, end } = this.parseServiceDates(
      detail.serviceDates,
      zone!.timeZone
    );
    if (!this.patchDates(start, end)) {
      return;
    }

    const { vehicleTypeValue, fuelTypeValue, brand, model, licensePlate } =
      this.extractVehicleData(detail.vehicleData);

    const mainProduct = detail.products?.[0];
    const {
      wantedProductId,
      providerAddressPayload,
      verticalReal,
      vertVal,
      cfg
    } = this.extractProductInfo(detail, mainProduct);

    const ori = detail.originContactInformation;
    const dst = detail.destinationContactInformation;
    const sameAdr = this.compareAddress(ori, dst);
    const sameCon = this.compareContact(ori, dst);

    this.activeVertical = verticalReal || null;
    this.activeVerticalValue = vertVal ?? null;

    this.patchBasicForm(
      { verticalReal, fixed: start.isSame(end) },
      { vehicleTypeValue, fuelTypeValue, brand, model, licensePlate },
      { ori, dst, sameAdr, sameCon, comments: detail.clientComments }
    );

    // Patch para los campos de documento de origen y destino
    this.form.patchValue(
      {
        originContactDocumentType: ori?.identityDocumentType ?? null,
        originContactDocumentNumber: ori?.identityDocumentNumber ?? '',
        destinationContactDocumentType: dst?.identityDocumentType ?? null,
        destinationContactDocumentNumber: dst?.identityDocumentNumber ?? ''
      },
      { emitEvent: false }
    );

    this.relaxPlaceIdValidationOnLoad();

    this.patchVerticalSpecificFields(cfg, vertVal, start);

    this.applySameToggles(sameAdr, sameCon);
    this.cd.detectChanges();

    this.loadProductsAndProviders(
      zone!.zoneId,
      vertVal,
      vehicleTypeValue,
      fuelTypeValue,
      wantedProductId,
      providerAddressPayload
    );

    this.enableVerticalInDuplicate();
    this.lockHeaderIfEdit();
  }

  /**
   * Desactiva temporalmente la validación de placeId al abrir el modal
   * (Edit / Duplicate) y la reactiva en cuanto el usuario modifica
   * la dirección.  También fuerza el borrado del placeId si el cambio
   * se produce de forma programática.
   */
  private relaxPlaceIdValidationOnLoad(): void {
    if (this.type_info === ModalMode.Add) return;

    this.togglePlaceIdValidation('origin', false);
    this.togglePlaceIdValidation('destination', false);

    this.watchAddressChanges('origin');
    this.watchAddressChanges('destination');
  }

  private togglePlaceIdValidation(
    key: 'origin' | 'destination',
    required: boolean
  ): void {
    const ctrl = this.form.get(
      key === 'origin' ? 'originAddressPlaceId' : 'destinationAddressPlaceId'
    )!;
    required ? ctrl.addValidators(Validators.required) : ctrl.clearValidators();
    ctrl.updateValueAndValidity({ emitEvent: false });
  }

  private watchAddressChanges(key: 'origin' | 'destination'): void {
    const addrCtrl = this.form.get(
      key === 'origin' ? 'originAddress' : 'destinationAddress'
    )!;

    addrCtrl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.clearPlaceId(key);
      this.togglePlaceIdValidation(key, true);
    });
  }

  private enableVerticalInDuplicate(): void {
    if (this.type_info === ModalMode.Duplicate) {
      const verticalCtrl = this.form.get('vertical');
      if (verticalCtrl?.disabled) {
        verticalCtrl.enable({ emitEvent: false });
      }
    }
  }

  /** 0) Asegura que detail existe, si no lanza alerta */
  private ensureDetailPresent(detail?: ServiceDetail): detail is ServiceDetail {
    if (!detail) {
      this.alerts
        .open(this.translate.instant('modals.add_service.missing_catalog'), {
          status: 'error'
        })
        .subscribe();
      this.off.dismiss();
      return false;
    }
    return true;
  }

  /** 1.a) Buscar zona */
  private findZone(zoneId: string): Zone | undefined {
    return this.zones.find((z) => z.zoneId === zoneId);
  }

  /** 1.b) Buscar cliente */
  private findClient(clientId: string): Client | undefined {
    return this.clients.find((c) => c.userId === clientId);
  }

  /** 1.c) Parchear zona y cliente, con alerta si falla */
  private patchZoneAndOptionalClient(detail: ServiceDetail): boolean {
    const zone = this.findZone(detail.zoneId);
    if (!zone) {
      this.alerts
        .open(this.translate.instant('modals.add_service.missing_catalog'), {
          status: 'error'
        })
        .subscribe();
      this.off.dismiss();
      return false;
    }
    // always patch zone
    this.form.patchValue({ zone }, { emitEvent: false });
    this.getCurrencyByZone(zone.zoneId);

    // only patch client if it's not the “zero GUID”
    if (detail.clientId !== ZERO_GUID) {
      const client = this.findClient(detail.clientId);
      if (!client) {
        this.alerts
          .open(this.translate.instant('modals.add_service.missing_catalog'), {
            status: 'error'
          })
          .subscribe();
        this.off.dismiss();
        return false;
      }
      this.form.patchValue({ clientName: client }, { emitEvent: false });
    }

    return true;
  }

  /** 2.a) Parsear fechas con Day.js y zona */
  private parseServiceDates(
    dates: {
      servicePickupDateRangeStart: ServiceDate;
      servicePickupDateRangeEnd: ServiceDate;
    },
    timeZone: string
  ): { start: dayjs.Dayjs; end: dayjs.Dayjs } {
    const { servicePickupDateRangeStart, servicePickupDateRangeEnd } = dates;
    const start = this.parseCompleteDate(
      servicePickupDateRangeStart?.completeDate ?? '',
      timeZone
    );
    const end = this.parseCompleteDate(
      servicePickupDateRangeEnd?.completeDate ?? '',
      timeZone
    );
    return { start, end };
  }

  /** 2.b) Parchear fechas en form o lanzar alerta */
  private patchDates(start: dayjs.Dayjs, end: dayjs.Dayjs): boolean {
    if (!start.isValid() || !end.isValid()) {
      this.alerts
        .open(
          this.translate.instant('modals.add_service.missing_service_time'),
          { status: 'error' }
        )
        .subscribe();
      return false;
    }
    const fixed = start.isSame(end);
    this.form.patchValue(
      {
        serviceStartDate: start.format('YYYY-MM-DD'),
        singleTime: fixed ? 'Hora fija' : 'Franja horaria',
        serviceStartHour: fixed ? start.format('HH:mm') : '',
        workdayStartTime: fixed ? '' : start.format('HH:mm'),
        workdayEndTime: fixed ? '' : end.format('HH:mm')
      },
      { emitEvent: false }
    );
    return true;
  }

  /** 3) Mapear datos de vehículo */
  private extractVehicleData(vehicleData: {
    vehicleType: string;
    fuelType: string;
    brand: string;
    model: string;
    licensePlate: string;
  }): {
    vehicleTypeValue: number | null;
    fuelTypeValue: number | null;
    brand: string;
    model: string;
    licensePlate: string;
  } {
    const vehicleTypeValue = this.resolveChassisFromDetail(
      vehicleData.vehicleType
    );
    const fuelTypeValue = this.fuelValueFromBackend(vehicleData.fuelType);

    return {
      vehicleTypeValue,
      fuelTypeValue,
      brand: vehicleData.brand,
      model: vehicleData.model,
      licensePlate: vehicleData.licensePlate
    };
  }

  /**
   * Chasis con el que rellenar el formulario a partir del detalle del servicio.
   *
   * Los servicios creados fuera de la plataforma (landing de COPEC) llegan como
   * UNDEFINED, que no está en el catálogo del desplegable. Al editar lo conservamos:
   * el campo está bloqueado y el back solo actualiza matrícula, marca y modelo, así
   * que exigir un chasis "válido" impedía guardar sin aportar nada. Al dar de alta o
   * duplicar seguimos devolviendo null para que se elija uno de verdad.
   */
  private resolveChassisFromDetail(vehicleTypeName: string): number | null {
    const cataloguedValue = this.chassisValueFromBackend(vehicleTypeName);
    if (cataloguedValue !== null) {
      return cataloguedValue;
    }

    if (
      this.type_info !== ModalMode.Edit ||
      vehicleTypeName !== UNDEFINED_CHASSIS_NAME
    ) {
      return null;
    }

    // Sin esta opción ng-select no sabría pintar el valor y mostraría el placeholder,
    // que se lee como "campo sin rellenar". Se crea un array nuevo para no tocar el
    // catálogo compartido.
    if (!this.VehicleChassisType.includes(UNDEFINED_CHASSIS_OPTION)) {
      this.VehicleChassisType = [
        ...this.VehicleChassisType,
        UNDEFINED_CHASSIS_OPTION
      ];
    }

    return VehicleChassisTypeEnum.UNDEFINED;
  }

  /** 4) Extraer info de producto + vertical */
  private extractProductInfo(
    detail: ServiceDetail,
    mainProduct?: { productId: string; serviceConfiguration?: string | object }
  ): {
    wantedProductId?: string;
    providerAddressPayload?: string;
    verticalReal: string;
    vertVal?: number;
    cfg: any;
  } {
    const wantedProductId = mainProduct?.productId;
    const providerAddressPayload = (detail as any).providerAddress;
    const verticalReal = this.resolveVerticalReal(detail) || '';
    const vertVal = this.mapVerticalValue(verticalReal);
    const cfg =
      typeof mainProduct?.serviceConfiguration === 'string'
        ? JSON.parse(mainProduct.serviceConfiguration)
        : (mainProduct?.serviceConfiguration ?? null);

    return {
      wantedProductId,
      providerAddressPayload,
      verticalReal,
      vertVal,
      cfg
    };
  }

  /** 6) Parcheo básico de formulario */
  private patchBasicForm(
    basics: { verticalReal: string; fixed: boolean },
    vehicle: {
      vehicleTypeValue: number | null;
      fuelTypeValue: number | null;
      brand: string;
      model: string;
      licensePlate: string;
    },
    contacts: {
      ori: ContactInformation;
      dst: ContactInformation;
      sameAdr: boolean;
      sameCon: boolean;
      comments?: string;
    }
  ): void {
    this.activeVertical = basics.verticalReal || null;
    this.form.patchValue(
      { vertical: basics.verticalReal || null },
      { emitEvent: false }
    );
    if (this.type_info !== ModalMode.Edit) {
      this.form.get('product')!.enable({ emitEvent: false });
    }

    // Vehículo y contactos
    const { ori, dst, sameAdr, sameCon, comments } = contacts;
    this.form.patchValue(
      {
        vehicleTypeName: vehicle.vehicleTypeValue,
        vehicleFuelName: vehicle.fuelTypeValue,
        vehicleBrand: vehicle.brand,
        vehicleModel: vehicle.model,
        licensePlate: vehicle.licensePlate,

        originAddress: ori.address,
        originAddressDetails: ori.addressDetails,
        originAddressGeopointLt: ori.addressPoint.Latitude,
        originAddressGeopointLn: ori.addressPoint.Longitude,
        sameOriginAddress: sameAdr,

        destinationAddress: dst.address,
        destinationAddressDetails: dst.addressDetails,
        destinationAddressGeopointLt: dst.addressPoint.Latitude,
        destinationAddressGeopointLn: dst.addressPoint.Longitude,

        isOriginContactExternal: ori.isExternalContact,
        originContactName: ori.name,
        originContactPhoneNumber: ori.phoneNumber,
        originalContactPhoneNumberPhoneIso: getCountryIsoFromPhone(
          ori.phoneNumber
        ),
        originContactEmailAddress: ori.emailAddress,

        isDestinationContactExternal: dst.isExternalContact,
        destinationContactName: dst.name,
        destinationContactPhoneNumber: dst.phoneNumber,
        destinationContactPhoneIso: getCountryIsoFromPhone(dst.phoneNumber),
        destinationContactEmailAddress: dst.emailAddress,

        comments: comments ?? ''
      },
      { emitEvent: false }
    );

    const sameCtrl = this.form.get('sameContactOriginDestiny')!;
    sameCtrl.setValue(sameCon, { emitEvent: true });
    this.applySameToggles(sameAdr, sameCon);
  }

  /** 7) Parcheo según vertical */
  private patchVerticalSpecificFields(
    cfg: any,
    vertVal?: number,
    start?: dayjs.Dayjs
  ): void {
    if (!cfg || vertVal == null) return;
    const seasonMap = (s: any) => {
      if (s == null || s === '') return null;
      if (typeof s === 'string') return s as any;
      return (['verano', 'invierno', '4estaciones'][Number(s)] as any) ?? null;
    };

    switch (vertVal) {
      case this.TYRESVERTICAL:
        this.patchTyresConfig(cfg, seasonMap);
        break;
      case this.REFUELINGVERTICAL:
        this.patchRefuelingConfig(cfg);
        break;
      case this.ITVVERTICAL:
        this.patchItvConfig(cfg, start!);
        break;
    }
  }

  private patchTyresConfig(cfg: any, seasonMap: (s: any) => any): void {
    if (cfg.RangeType === 'custom' && Array.isArray(cfg.CustomTyres)) {
      this.tyresArray.clear();
      cfg.CustomTyres.forEach((t: any, i: number) => {
        this.addCustomTyre();
        this.tyresArray.at(i).patchValue(
          {
            tyreSeason: seasonMap(t.Season),
            withStuds: !!t.IsAntiPuncture,
            exchangeOld: !!t.TyresPermutation,
            tyreWidth: String(t.Width ?? ''),
            tyreSeries: String(t.Series ?? ''),
            tyreDiameter: String(t.Diameter ?? ''),
            tyreLoadIndex: String(t.LoadIndex ?? ''),
            tyreSpeedIndex: t.SpeedIndex ?? '',
            tyreBrand: t.FavoriteBrand ?? '',
            tyreModel: t.Model ?? '',
            tyreCount: Number(t.NumberOfTyres ?? 1),
            tyreUnitCost: Number(t.TyresPrice ?? 0),
            needAlignment: !!(t.TyresAlignment ?? t.NeedAlignment)
          },
          { emitEvent: false }
        );
      });
      this.form.patchValue(
        { tyreOrderType: 'custom', serviceOption: cfg.ServiceOption ?? null },
        { emitEvent: false }
      );
    } else if (cfg.Width != null || cfg.Season != null || cfg.RangeType != null) {
      this.form.patchValue(
        {
          // Los servicios de COPEC no traen tipo de pedido y llega como cadena vacía, que
          // `??` no cubre: ningún radio quedaba marcado y, al ser obligatorio, el
          // formulario no se podía guardar. Se cae al pedido de configuración única, que
          // es el que corresponde a este bloque de campos.
          tyreOrderType: cfg.RangeType || '4iguales',
          tyreSeason: seasonMap(cfg.Season),
          withStuds: !!cfg.IsAntiPuncture,
          exchangeOld: !!cfg.TyresPermutation,
          tyreWidth: String(cfg.Width ?? ''),
          tyreSeries: String(cfg.Series ?? ''),
          tyreDiameter: String(cfg.Diameter ?? ''),
          tyreLoadIndex: String(cfg.LoadIndex ?? ''),
          tyreSpeedIndex: cfg.SpeedIndex ?? '',
          tyreBrand: cfg.FavoriteBrand ?? '',
          tyreModel: cfg.Model ?? '',
          tyreCount: Number(cfg.NumberOfTyres ?? 1),
          tyreUnitCost: Number(cfg.TyresPrice ?? 0),
          needAlignment: !!(cfg.TyresAlignment ?? cfg.NeedAlignment),
          serviceOption: cfg.ServiceOption ?? null
        },
        { emitEvent: false }
      );
    }
  }

  private patchRefuelingConfig(cfg: any): void {
    this.form.patchValue(
      {
        monetaryAmount: cfg.MonetaryAmount ?? null,
        vehicleFuelType: this.mapFuelTypeEnumNumber(cfg.FuelType)
      },
      { emitEvent: false }
    );
  }

  private patchItvConfig(cfg: any, start: dayjs.Dayjs): void {
    this.form.patchValue(
      {
        appointmentCode: cfg.AppointmentCode ?? '',
        isInspectionPaid: !!cfg.IsInspectionPaid,
        vehicleStatus: cfg.VehicleStatus?.toString() ?? '',
        appointmentDate: cfg.AppointmentCode ? start.format('YYYY-MM-DD') : '',
        appointmentHour: cfg.AppointmentCode ? start.format('HH:mm') : ''
      },
      { emitEvent: false }
    );
  }

  /** 8) Aplicar toggles “misma dirección/contacto” */
  private applySameToggles(sameAdr: boolean, sameCon: boolean): void {
    const destAddr = this.form.get('destinationAddress')!;
    const destDetl = this.form.get('destinationAddressDetails')!;
    sameAdr
      ? destAddr.disable({ emitEvent: false })
      : destAddr.enable({ emitEvent: false });
    sameAdr
      ? destDetl.disable({ emitEvent: false })
      : destDetl.enable({ emitEvent: false });

    const contactKeys: Array<keyof ServiceForm> = [
      'destinationContactName',
      'destinationContactPhoneNumber',
      'destinationContactEmailAddress',
      'isDestinationContactExternal',
      'destinationContactDocumentType',
      'destinationContactDocumentNumber'
    ];
    contactKeys.forEach((k) => {
      const ctrl = this.form.get(k)!;
      sameCon
        ? ctrl.disable({ emitEvent: false })
        : ctrl.enable({ emitEvent: false });
    });
  }

  private normalizeAddress(addr: string): string {
    return addr
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/\s+/g, ' ')
      .trim();
  }

  /**
   * Carga productos y proveedores y preselecciona el proveedor correcto
   * buscando primero por ID, luego por dirección, y siempre mostrando
   * el selector (aunque deshabilitado) cuando no hay coincidencias.
   */
  private loadProductsAndProviders(
    zoneId: string,
    vertVal?: number,
    vehicleTypeValue?: VehicleChassisTypeEnum | null,
    fuelTypeValue?: VehicleFuelTypeEnum | null,
    wantedProductId?: string,
    providerAddressPayload?: string
  ): void {
    // 1) Preparar filtros de tipo de vehículo / combustible
    const chassisType = this.mapVehicleTypeInternal(
      vehicleTypeValue ?? undefined
    );
    const fuelType = this.mapFuelTypeInternal(fuelTypeValue ?? undefined);

    this.loadingProducts = true;

    // 2) Obtener productos
    this.hs
      .getProducts({
        zoneId,
        verticalType: vertVal!,
        chassisType,
        fuelType,
        includeToppings: false
      })
      .pipe(
        take(1),
        tap((resp: GetProductsResponse) => {
          const mappedProducts = resp.products.map<ProductUi>((p) => ({
            ...p,
            traduction: p.productLocalizationCode
              ? this.translate.instant(
                  `comercial.products.${p.productLocalizationCode}`
                )
              : p.productLocalizationCode
          }));

          this.productsOfVertical = this.filterProductsForCurrentTenant(
            mappedProducts
          );

          this.verticals = Array.from(
            new Set(
              this.productsOfVertical
                .map((p) => this.mapVertical(Number(p.verticalType)))
                .filter(Boolean)
            )
          );

          this.productsName = this.activeVertical
            ? this.getProductsForCurrentTenantByVertical(vertVal).map(
                (p) => p.traduction
              )
            : [];

          if (wantedProductId) {
            const match = this.getProductsForCurrentTenantByVertical(
              vertVal
            ).find(
              (p) => p.productId === wantedProductId
            );
            if (match) {
              this.form.patchValue(
                { product: match.traduction },
                { emitEvent: false }
              );
              this.activeProductValue = Number(match.verticalType);
              if (this.type_info !== ModalMode.Edit) {
                this.form.get('product')!.enable({ emitEvent: false });
              }
              this.enforceTransferContactRules(match);
            }
          }
        }),
        finalize(() => (this.loadingProducts = false)),

        switchMap(() => {
          this.providersLoading = true;
          return this.hs
            .getProviders(zoneId)
            .pipe(finalize(() => (this.providersLoading = false)));
        })
      )
      .subscribe({
        next: ({ providersInZone }: GetServiceDetailProviderResponse) => {
          const productSel =
            this.productsOfVertical.find(
              (p) => p.traduction === this.form.get('product')!.value
            ) ??
            this.productsOfVertical.find(
              (p) => p.productId === wantedProductId
            );

          let compatProviders: ProviderServiceDetail[] = productSel
            ? providersInZone.filter((pr) =>
                pr.productIds.includes(productSel.productId)
              )
            : providersInZone;

          const providerCtrl = this.form.get('provider')!;
          const ZERO_GUID = '00000000-0000-0000-0000-000000000000';
          const detail = this.service_info?.products?.[0];

          this.showProvider = true;

          if (detail) {
            const { providerId, address } = detail;

            if (providerId && providerId !== ZERO_GUID) {
              const byId = providersInZone.find((p) => p.id === providerId);
              if (byId) {
                if (!compatProviders.some((p) => p.id === byId.id)) {
                  compatProviders = [byId, ...compatProviders];
                }
                providerCtrl.patchValue(byId.name, { emitEvent: false });
              }
            }

            if (!providerCtrl.value && address) {
              const normAddr = this.normalizeAddress(address);
              const byAddr = providersInZone.find(
                (p) => this.normalizeAddress(p.address) === normAddr
              );
              if (byAddr) {
                if (!compatProviders.some((p) => p.id === byAddr.id)) {
                  compatProviders = [byAddr, ...compatProviders];
                }
                providerCtrl.patchValue(byAddr.name, { emitEvent: false });
              }
            }
          }

          if (!providerCtrl.value && providerAddressPayload) {
            const normDup = this.normalizeAddress(providerAddressPayload);
            const dup = providersInZone.find(
              (p) => this.normalizeAddress(p.address) === normDup
            );
            if (dup) {
              if (!compatProviders.some((p) => p.id === dup.id)) {
                compatProviders = [dup, ...compatProviders];
              }
              providerCtrl.patchValue(dup.name, { emitEvent: false });
            }
          }

          this.providers = compatProviders;
          this.providersName = compatProviders.map((p) => p.name);
          this.providerPlaceholder = compatProviders.length
            ? 'modals.add_service.select_provider'
            : 'modals.add_service.no_providers_available_zone';

          if (compatProviders.length) {
            providerCtrl.enable({ emitEvent: false });
          } else {
            providerCtrl.disable({ emitEvent: false });
          }

          this.cd.detectChanges();
        },
        error: (err) => console.error('Error loading products/providers', err)
      });
  }

  private parseCompleteDate(raw: string, fallbackTz: string): dayjs.Dayjs {
    const [isoPart, tzName = ''] = raw.trim().split(' ');
    const isoFixed = isoPart.replace(/([+-]\d{2})(?=$)/, '$1:00');
    let d = dayjs(isoFixed);
    const zoneToUse = tzName || fallbackTz || 'Europe/Madrid';
    if (tzName) {
      // El instante ya lleva su offset (p.ej. «-04:00»), así que convertimos
      // a la zona del servicio SIN keepLocalTime para obtener su hora de pared
      // real (15:00 en Santiago), independientemente de la zona del navegador.
      d = d.tz(zoneToUse);
    }

    if (!d.isValid()) {
      d = dayjs();
    }

    return d.startOf('minute');
  }

  private getProductsForCurrentTenantByVertical(vertVal?: number): ProductUi[] {
    if (vertVal == null) {
      return [];
    }

    const productsInVertical = this.productsOfVertical.filter(
      (p) => Number(p.verticalType) === vertVal
    );

    if (!this.isOkMobilityTenant) {
      return productsInVertical;
    }

    return productsInVertical.filter((p) => {
      return this.isTransferProduct(p);
    });
  }

  private filterProductsForCurrentTenant(products: ProductUi[]): ProductUi[] {
    if (!this.isOkMobilityTenant) {
      return products;
    }

    return products.filter((p) => this.isTransferProduct(p));
  }

  private isTransferProduct(p: ProductUi): boolean {
    const verticalType = Number(p.verticalType);
    const productType = String(p.productType ?? '').toLowerCase();
    const productKey = String(
      (p as ProductUi & { productKey?: string }).productKey ?? ''
    ).toLowerCase();
    const localizationKey = String(p.productLocalizationCode ?? '').toLowerCase();
    const productName = String(p.productName ?? '').toLowerCase();

    return (
      verticalType === 1 ||
      productType.includes('transfer') ||
      productKey.includes('transfer') ||
      localizationKey.includes('transfer') ||
      productName.includes('transfer')
    );
  }

  ngOnDestroy(): void {
    this.autocompleteListeners.forEach((l) => l.remove());
    this.destroy$.next();
    this.destroy$.complete();
  }

  private lockHeaderIfEdit(): void {
    if (this.type_info !== ModalMode.Edit) {
      return;
    }

    [
      'zone',
      'clientName',
      'vertical',
      'product',
      'vehicleTypeName',
      'vehicleFuelName'
    ].forEach((cn) => this.form.get(cn)?.disable({ emitEvent: false }));

    [
      'serviceStartDate',
      'singleTime',
      'serviceStartHour',
      'workdayStartTime',
      'workdayEndTime'
    ].forEach((cn) => this.form.get(cn)?.enable({ emitEvent: false }));
  }

  private clearTyreSection(): void {
    const extrasCtrl = this.form.get('extras') as FormArray<FormGroup>;

    extrasCtrl.clear({ emitEvent: false });
    extrasCtrl.disable({ emitEvent: false });

    this.form.patchValue(
      {
        tyreOrderType: null,
        tyreSeason: null,
        withStuds: false,
        exchangeOld: false,
        tyreWidth: '',
        tyreSeries: '',
        tyreDiameter: '',
        tyreLoadIndex: '',
        tyreSpeedIndex: '',
        tyreBrand: '',
        tyreModel: '',
        tyreCount: 1,
        tyreUnitCost: '',
        serviceOption: null,
        needAlignment: false
      },
      { emitEvent: false }
    );

    [
      'tyreOrderType',
      'tyreSeason',
      'tyreWidth',
      'tyreSeries',
      'tyreDiameter',
      'tyreLoadIndex',
      'tyreSpeedIndex',
      'tyreBrand',
      'tyreModel',
      'tyreCount',
      'tyreUnitCost',
      'serviceOption',
      'needAlignment'
    ].forEach((cn) => this.form.get(cn)?.markAsPristine());

    this.form.updateValueAndValidity({ emitEvent: false });
  }

  private markTyresErrorsOnSave(): void {
    const isTyres =
      this.mapVerticalValue(this.form.get('vertical')?.value ?? '') ===
      this.TYRESVERTICAL;
    if (!isTyres) return;

    const tyreOrderTypeCtrl = this.form.get('tyreOrderType');
    const serviceOptionCtrl = this.form.get('serviceOption');
    const isCustom = tyreOrderTypeCtrl?.value === 'custom';

    tyreOrderTypeCtrl?.markAsTouched();
    serviceOptionCtrl?.markAsTouched();
    tyreOrderTypeCtrl?.updateValueAndValidity({ emitEvent: false });
    serviceOptionCtrl?.updateValueAndValidity({ emitEvent: false });

    if (isCustom) {
      this.tyresArray.controls.forEach((ctrl) => ctrl.markAllAsTouched());
      return;
    }

    const tyreSeasonCtrl = this.form.get('tyreSeason');
    tyreSeasonCtrl?.markAsTouched();
    tyreSeasonCtrl?.updateValueAndValidity({ emitEvent: false });
  }

  openDeliveryNoteUploadModal(): void {
    const modalRef = this.modalService.open(ModalUploadDeliveryNoteComponent, {
      centered: true,
      windowClass: 'modal-delivery-note-wide',
      size: 'lg'
    });

    const clientId = this.form.get('clientName')?.value?.userId || '';
    const orderHash = this.service_info?.orderHash || '';

    modalRef.componentInstance.clientId = clientId;
    modalRef.componentInstance.orderHash = orderHash;
    modalRef.componentInstance.serviceId = '';

    modalRef.result.then(
      (result: any) => {
        if (result?.deliveryNoteResult) {
          this.deliveryNoteId = result.deliveryNoteResult.documentId;
          this.deliveryNoteName = result.deliveryNoteResult.documentName;
          this.deliveryNoteSize = result.deliveryNoteResult.documentSize || '';
          this.deliveryNoteDate = result.deliveryNoteResult.uploadedDate || '';
          this.form.get('deliveryNoteId')?.setValue(this.deliveryNoteId, { emitEvent: false });
          this.cd.detectChanges();
        }
      },
      () => {
      }
    );
  }

  removeDeliveryNote(): void {
    if (!this.deliveryNoteId) return;

    const confirmModal = this.modalService.open(ModalConfirmComponent, { centered: true });
    confirmModal.componentInstance.title = this.translate.instant('modals.add_service.delete_delivery_note_title');
    confirmModal.componentInstance.message = this.translate.instant('modals.add_service.delete_delivery_note_message');

    confirmModal.result
      .then(() => {
        this.deliveryNoteId = '';
        this.deliveryNoteName = '';
        this.deliveryNoteSize = '';
        this.deliveryNoteDate = '';
        this.form.get('deliveryNoteId')?.setValue('', { emitEvent: false });
        this.cd.detectChanges();
      })
      .catch(() => {});
  }

  save(): void {
    this.markTyresErrorsOnSave();

    if (this.form.invalid) {
      const invalidControls = Object.keys(this.form.controls).filter(
        (key) => this.form.get(key)!.invalid
      );
      console.warn(
        '🛑 Formulario inválido:',
        this.form.status,
        '| Controles inválidos →',
        invalidControls
      );
      this.form.markAllAsTouched();
      return;
    }

    this.isSaving = true;

    let operation$;
    if (this.type_info === ModalMode.Edit) {
      if (!this.service_info?.orderHash) {
        console.error('Falta orderHash para actualizar');
        this.handleError();
        this.resetUiAfterError();
        return;
      }

      const dto = this.buildUpdatePayload();

      if (dto == null) {
        console.error('buildUpdatePayload devolvió null');
        this.handleError();
        this.resetUiAfterError();
        return;
      }
      operation$ = this.hs.updateService(dto);
    } else {
      const dto = this.buildCreatePayload();

      if (dto == null) {
        console.error('buildCreatePayload devolvió null');
        this.handleError();
        this.resetUiAfterError();
        return;
      }
      operation$ = this.hs.addService(dto);
    }

    operation$
      .pipe(
        take(1),
        tap((res: OperationResult) => {
          if (res.wasSuccessful) {
            this.handleSuccess();
          } else {
            this.alerts
              .open(
                this.translate.instant('modals.add_service.operation_failed'),
                {
                  status: 'error'
                }
              )
              .subscribe();
            this.isSaving = false;
          }
        }),
        catchError((err) => {
          this.handleError(err);
          return throwError(() => err);
        }),
        finalize(() => this.resetUiAfterError())
      )
      .subscribe();
  }

  private resetUiAfterError(): void {
    this.isSaving = false;
  }

  private enforceTransferContactRules(selectedProduct: ProductUi | null): void {
    const TRANSFER_VERTICAL = 1;
    const currentVerticalValue: number | null =
      this.activeVerticalValue ??
      this.mapVerticalValue(this.form.get('vertical')?.value ?? '') ??
      null;

    // Si no es Transfer -> revertir estado (habilitar controles)
    if (currentVerticalValue !== TRANSFER_VERTICAL) {
      this.revertContactExternalControls();
      return;
    }

    const codeOrName: string = (
      selectedProduct?.productLocalizationCode ??
      selectedProduct?.productName ??
      (this.form.get('product')?.value as string | null) ??
      ''
    )
      .toString()
      .toLowerCase();

    const isCollect = codeOrName.includes('collect');
    const isReturn =
      codeOrName.includes('return') || codeOrName.includes('delivery');

    const originCtrl = this.form.get('isOriginContactExternal')!;
    const destCtrl = this.form.get('isDestinationContactExternal')!;

    if (isCollect) {
      // Recogida: origen externo, destino interno
      originCtrl.setValue(true, { emitEvent: false });
      destCtrl.setValue(false, { emitEvent: false });
      originCtrl.disable({ emitEvent: false });
      destCtrl.disable({ emitEvent: false });
    } else if (isReturn) {
      // Entrega: origen interno, destino externo
      originCtrl.setValue(false, { emitEvent: false });
      destCtrl.setValue(true, { emitEvent: false });
      originCtrl.disable({ emitEvent: false });
      destCtrl.disable({ emitEvent: false });
    } else {
      // Otros productos: permitir edición normal
      this.revertContactExternalControls();
    }

    try {
      this.cd.detectChanges();
    } catch {
      // noop
    }
  }

  private revertContactExternalControls(): void {
    const originCtrl = this.form.get('isOriginContactExternal')!;
    const destCtrl = this.form.get('isDestinationContactExternal')!;

    if (originCtrl.disabled) {
      originCtrl.enable({ emitEvent: false });
    }
    if (destCtrl.disabled) {
      destCtrl.enable({ emitEvent: false });
    }

    try {
      this.cd.detectChanges();
    } catch {
      // noop
    }
  }

  private getTransferProductRole(
    selectedProduct?: ProductUi | null
  ): 'collect' | 'return' | null {
    const prod: ProductUi | undefined =
      selectedProduct ??
      this.productsOfVertical.find(
        (p) =>
          p.traduction === (this.form.get('product')?.value as string | null)
      );

    if (!prod) return null;

    const code = (
      prod.productLocalizationCode ??
      prod.productName ??
      ''
    ).toLowerCase();

    if (code.includes('collect')) return 'collect';
    if (code.includes('return') || code.includes('delivery')) return 'return';
    return null;
  }

  private buildCreatePayload(): CreateServiceRequestDto | null {
    const formValue = this.form.getRawValue() as ServiceForm;

    const zone = this.validateAndGetZone(formValue.zone?.zoneId);
    if (!zone) return null;
    
    // Hay casos en los que el ID del cliente no viene rellenado ej. servicios que se crean desde la landing de COPEC
    const clientId =
      formValue.clientName?.userId ?? this.service_info?.clientId ?? '';

    // 3) Fechas servicio (ISO + TZ)
    const { serviceStartDate, serviceEndDate } = this.buildServiceDates(
      formValue,
      zone.timeZone
    );
    if (!serviceStartDate) return null; // internamente lanza alerta

    // 4) Producto y proveedor
    const productUI = this.validateAndGetProduct(formValue.product);
    if (!productUI) return null;
    const providerId = this.providers.find(
      (p) => p.name === formValue.provider
    )?.id;

    // 5) Vehículo
    const { vehicleType, fuelType } = this.validateAndGetVehicleMeta(
      formValue.vehicleTypeName,
      formValue.vehicleFuelName
    );

    if (vehicleType == null || fuelType == null) return null;

    // 6) Puntos GPS
    const originPoint = this.mkPoint(
      formValue.originAddressGeopointLt,
      formValue.originAddressGeopointLn
    );
    const destinationPoint = this.mkPoint(
      formValue.destinationAddressGeopointLt,
      formValue.destinationAddressGeopointLn
    );

    // 7) ServiceConfiguration según vertical
    const serviceConfiguration = this.buildServiceConfiguration(formValue);

    const bp: {
      productId: string;
      providerId?: string;
      serviceConfiguration?: string;
    } = {
      productId: productUI.productId // siempre
    };

    // providerId: solo si realmente existe y es no vacío
    if (providerId) {
      bp.providerId = providerId; // ya es string
    }

    // serviceConfiguration: solo si tenemos propiedades dentro
    if (serviceConfiguration && Object.keys(serviceConfiguration).length > 0) {
      bp.serviceConfiguration = JSON.stringify(serviceConfiguration);
    }

    // 8) DTO final
    const dto: CreateServiceRequestDto = {
      zoneId: zone.zoneId,
      clientId,
      comments: formValue.comments,

      // Direcciones
      originAddress: formValue.originAddress,
      originAddressDetails: formValue.originAddressDetails,
      originAddressGeopoint: originPoint,
      destinationAddress: formValue.destinationAddress,
      destinationAddressDetails: formValue.destinationAddressDetails,
      destinationAddressGeopoint: destinationPoint,

      // Contactos
      originContactData: this.buildContactDto(
        formValue.originContactName,
        formValue.originContactPhoneNumber,
        formValue.originContactEmailAddress,
        formValue.isOriginContactExternal,
        formValue.originAddress,
        formValue.originAddressDetails,
        originPoint,
        formValue.originContactDocumentType,
        formValue.originContactDocumentNumber
      ),
      destinationContactData: this.buildContactDto(
        formValue.destinationContactName,
        formValue.destinationContactPhoneNumber,
        formValue.destinationContactEmailAddress,
        formValue.isDestinationContactExternal,
        formValue.destinationAddress,
        formValue.destinationAddressDetails,
        destinationPoint,
        formValue.destinationContactDocumentType,
        formValue.destinationContactDocumentNumber
      ),

      // Vehículo
      vehicleData: {
        vehicleType,
        brand: formValue.vehicleBrand,
        model: formValue.vehicleModel,
        licensePlate: formValue.licensePlate,
        fuelType
      },

      // Logística
      transferType: TransferTypeEnum.COLLECTIVE_TOW_TRUCK,
      serviceStartDate,
      serviceEndDate,

      bookedProducts: [bp],

      pdfId: formValue.deliveryNoteId || undefined,

      customProductPrice: formValue.customProductPrice != null ? Number(formValue.customProductPrice) : null,
      customCaflerFee: formValue.customCaflerFee != null ? Number(formValue.customCaflerFee) : null,
      addFixedFee: !!formValue.addFixedFee,
      addUrgencyFee: !!formValue.addUrgencyFee,
  
      isPoolService: !!formValue.isPoolService,
      customPoolPrice: formValue.isPoolService && formValue.customPoolPrice != null ? Number(formValue.customPoolPrice) : null
    };


    return dto;
  }

  /** Valida zona y muestra alerta si falta */
  private validateAndGetZone(zoneId?: string): Zone | null {
    if (!zoneId) {
      this.alertError('modals.add_service.invalid_zone');
      return null;
    }
    return this.zones.find((z) => z.zoneId === zoneId)!;
  }

  /** Construye fechas de servicio o muestra alerta */
  private buildServiceDates(
    f: ServiceForm,
    tz: string
  ): { serviceStartDate?: ServiceDate; serviceEndDate?: ServiceDate } {
    const toSvcDate = (d: string, t: string) => ({
      completeDate: `${(dayjs as any)
        .tz(`${d}T${t}`, tz)
        .format('YYYY-MM-DDTHH:mm:ssZ')} ${tz}`
    });
    const fixed = f.singleTime === 'Hora fija';
    const startTime = fixed ? f.serviceStartHour : f.workdayStartTime;
    const endTime = fixed ? f.serviceStartHour : f.workdayEndTime;
    if (!startTime || !endTime) {
      this.alertError('modals.add_service.missing_service_time');
      return {};
    }
    return {
      serviceStartDate: toSvcDate(f.serviceStartDate, startTime),
      serviceEndDate: toSvcDate(f.serviceStartDate, endTime)
    };
  }

  /** Valida producto */
  private validateAndGetProduct(productName: string | null) {
    const productUI = this.productsOfVertical.find(
      (p) => p.traduction === productName
    );
    if (!productUI) this.alertError('modals.add_service.invalid_product');
    return productUI;
  }

  /** Valida y mapea vehículo */
  private validateAndGetVehicleMeta(
    chassisValue: number | null,
    fuelValue: number | null
  ): { vehicleType?: VehicleChassisTypeEnum; fuelType?: VehicleFuelTypeEnum } {
    const chassisMeta = vehicleChassisType.find(
      (c) => c.value === chassisValue
    );
    const fuelMeta = vehicleFuelType.find((v) => v.value === fuelValue);

    // UNDEFINED es un valor legítimo del back (servicios creados sin chasis), pero no
    // está en el catálogo porque no debe ofrecerse en el desplegable. Solo llega hasta
    // aquí editando, donde el campo está bloqueado y el back lo ignora.
    const isUndefinedChassis =
      chassisValue === VehicleChassisTypeEnum.UNDEFINED;

    if ((!chassisMeta && !isUndefinedChassis) || !fuelMeta) {
      this.alertError('modals.add_service.invalid_vehicle_type');
      return {};
    }
    return {
      vehicleType: isUndefinedChassis
        ? VehicleChassisTypeEnum.UNDEFINED
        : VehicleChassisTypeEnum[
            chassisMeta!.name as keyof typeof VehicleChassisTypeEnum
          ],
      fuelType:
        VehicleFuelTypeEnum[fuelMeta.name as keyof typeof VehicleFuelTypeEnum]
    };
  }

  /** Crea punto GPS */
  private mkPoint(lat: number, lng: number): AddressPoint {
    return { Latitude: lat, Longitude: lng, Srid: 4326 };
  }

  /** Construye serviceConfiguration según vertical */
  private buildServiceConfiguration(
    f: ServiceForm
  ): ServiceConfiguration | null | undefined {
    if (!f.vertical) {
      this.alertError('modals.add_service.no_vertical');
      return null;
    }
    const vertVal = this.mapVerticalValue(f.vertical);
    if (vertVal === undefined) {
      this.alertError('modals.add_service.no_vertical');
      return null;
    }

    const n2u = <T>(v: T | null | ''): T | undefined =>
      v ? (v as T) : undefined;
    switch (vertVal) {
      case this.TYRESVERTICAL:
        return this.buildTyresConfig(f, n2u);
      case this.REFUELINGVERTICAL:
        return {
          MonetaryAmount: f.monetaryAmount
            ? +String(f.monetaryAmount).replace(/[^\d.]/g, '')
            : undefined,
          FuelType: this.mapFuelTypeEnumNumber(f.vehicleFuelType)
        };
      case this.ITVVERTICAL:
        return {
          AppointmentCode: n2u(f.appointmentCode),
          IsInspectionPaid: f.isInspectionPaid || undefined,
          VehicleStatus:
            typeof f.vehicleStatus === 'number'
              ? (f.vehicleStatus as 0 | 1 | 2)
              : undefined
        };
      default:
        return undefined;
    }
  }

  /** Configuración neumáticos */

  /**
   * Medida del neumático como número, u omitida si no lo es.
   *
   * Antes se hacía `+valor` a secas: con un texto daba NaN, que `JSON.stringify` serializa
   * como `null`, y el backend interpretaba eso como "sin medida" y la borraba. Los
   * validadores del formulario ya no dejan llegar aquí un texto, pero omitir el campo es
   * preferible a mandar un valor que no significa nada.
   */
  private tyreNumberOrUndefined(value: unknown): number | undefined {
    if (value === null || value === undefined || value === '') {
      return undefined;
    }

    const parsed = Number(value);

    return Number.isFinite(parsed) ? parsed : undefined;
  }

  private buildTyresConfig(
    f: ServiceForm,
    n2u: <T>(v: T | null | '') => T | undefined
  ): ServiceConfiguration {
    // Siempre metemos los neumáticos en un array CustomTyres
    const isCustom = f.tyreOrderType === 'custom';

    // Para "custom" usamos el FormArray; para "4iguales"/"2x2" creamos uno ficticio de 1 elemento
    const rawTyres = isCustom
      ? (this.form.get('extras') as FormArray<FormGroup>).controls.map((c) =>
          c.getRawValue()
        )
      : [f];

    const CustomTyres = rawTyres.map((t: any) => ({
      Width: this.tyreNumberOrUndefined(t.tyreWidth),
      Series: this.tyreNumberOrUndefined(t.tyreSeries),
      Diameter: this.tyreNumberOrUndefined(t.tyreDiameter),
      SpeedIndex: n2u(t.tyreSpeedIndex),
      LoadIndex: this.tyreNumberOrUndefined(t.tyreLoadIndex),
      Season: n2u(t.tyreSeason),
      FavoriteBrand: n2u(t.tyreBrand),
      Model: n2u(t.tyreModel),
      RangeType: isCustom
        ? 'custom'
        : (f.tyreOrderType as '4iguales' | '2x2' | undefined),
      NumberOfTyres: +t.tyreCount || undefined,
      IsAntiPuncture: t.withStuds || undefined,
      TyresPermutation: t.exchangeOld || undefined,
      TyresAlignment: t.needAlignment || undefined,
      TyresPrice: +t.tyreUnitCost || undefined
    }));

    return {
      CustomTyres,
      ServiceOption: n2u(f.serviceOption)
    };
  }
 
  private buildContactDto(
    name: string,
    phone: string,
    email: string,
    external: boolean,
    address: string,
    details: string,
    point: AddressPoint,
    identityDocumentType?: any,
    identityDocumentNumber?: string
  ) {
    return {
      name,
      phoneNumber: phone,
      emailAddress: email,
      isExternalContact: external,
      address,
      addressDetails: details,
      addressPoint: point,
      identityDocumentType: identityDocumentType ?? null,
      identityDocumentNumber: identityDocumentNumber ?? ''
    };
  }

  /** Alerta genérica desde translateKey */
  private alertError(translateKey: string): void {
    this.alerts
      .open(this.translate.instant(translateKey), { status: 'error' })
      .subscribe();
  }

  private mapFuelTypeEnumNumber(real: string | undefined): number | undefined {
    if (!real) return undefined;
    const key = vehicleFuelType.find((v) => v.real_name === real)?.name as
      | keyof typeof VehicleFuelTypeEnum
      | undefined;

    return key ? VehicleFuelTypeEnum[key] : undefined;
  }

  private buildUpdatePayload(): UpdateOrderRequestDto | null {
    const createDto = this.buildCreatePayload();
    if (!createDto) {
      return null;
    }

    return {
      orderHash: this.service_info!.orderHash,
      clientId: this.service_info!.clientId,
      comments: createDto.comments,
      serviceStartDate: createDto.serviceStartDate,
      serviceEndDate: createDto.serviceEndDate,
      vehicleData: createDto.vehicleData,
      originContactData: createDto.originContactData,
      destinationContactData: createDto.destinationContactData,
      toppings: createDto.bookedProducts.map((bp) => ({
        productId: bp.productId,
        providerId: bp.providerId,
        ...(bp.serviceConfiguration && {
          serviceConfiguration: bp.serviceConfiguration
        })
      }))
    };
  }

  capitalize(s: string) {
    return s.charAt(0).toUpperCase() + s.slice(1);
  }

  private reloadProducts(): void {
    const zone: Zone | null = this.form.get('zone')?.value;
    if (!zone) {
      // Si no hay zona, limpiamos todos los listados
      this.productsOfVertical = [];
      this.verticals = [];
      this.productsName = [];
      return;
    }
    this.loadingProducts = true;

    const vertical: string | null = this.form.get('vertical')?.value;
    const chassisType = this.mapVehicleTypeInternal(
      this.form.get('vehicleTypeName')?.value
    );
    const fuelType = this.mapFuelTypeInternal(
      this.form.get('vehicleFuelName')?.value
    );

    const payload = {
      zoneId: zone.zoneId,
      verticalType: vertical ? this.mapVerticalValue(vertical) : undefined,
      chassisType,
      fuelType,
      includeToppings: false
    };

    this.hs
      .getProducts(payload)
      .pipe(
        catchError((err) => {
          this.loadingProducts = false;
          console.error('Error cargando productos', err);
          this.alerts
            .open(
              this.translate.instant('modals.add_service.load_products') ||
                'Error al cargar productos',
              {
                status: 'error'
              }
            )
            .subscribe();
          return of({ products: [] as ProductUi[] });
        })
      )
      .subscribe(({ products }) => {
        // 1. Transformación a ProductUi con traducción
        const mappedProducts = products.map((p) => ({
          ...p,
          traduction:
            p.productLocalizationCode != null
              ? this.translate.instant(
                  `comercial.products.${p.productLocalizationCode}`
                )
              : ''
        }));

        this.productsOfVertical = this.filterProductsForCurrentTenant(
          mappedProducts
        );

        // 2. Verticales disponibles (sin duplicados)
        this.verticals = Array.from(
          new Set(
            this.productsOfVertical
              .map((p) =>
                this.mapVertical(
                  typeof p.verticalType === 'number'
                    ? p.verticalType
                    : Number(p.verticalType)
                )
              )
              .filter((v) => !!v)
          )
        );

        // 3. Si hay vertical seleccionada, filtramos nombres de producto
        if (vertical) {
          const vValue = this.mapVerticalValue(vertical);
          this.productsName = this.getProductsForCurrentTenantByVertical(
            vValue
          ).map((p) => p.traduction);
        } else {
          this.productsName = [];
        }
        this.loadingProducts = false;
      });
  }

  private copyOriginToDestination(): void {
    const {
      originAddress,
      originAddressDetails,
      originAddressGeopointLt,
      originAddressGeopointLn,
      originAddressPlaceId
    } = this.form.value as ServiceForm;

    const patch: Partial<ServiceForm> = {
      destinationAddress: originAddress,
      destinationAddressDetails: originAddressDetails,
      destinationAddressGeopointLt: originAddressGeopointLt,
      destinationAddressGeopointLn: originAddressGeopointLn,
      destinationAddressPlaceId: originAddressPlaceId
    };

    this.form.patchValue(patch, { emitEvent: false });
  }

  private copyOriginContactToDest(): void {
    const f = this.form.getRawValue() as ServiceForm;

    const transferRole = this.getTransferProductRole();

    let destinationIsExternal: boolean;
    if (transferRole === 'collect') {
      destinationIsExternal = false;
    } else if (transferRole === 'return') {
      destinationIsExternal = true;
    } else {
      destinationIsExternal = !!f.isOriginContactExternal;
    }

    const patch: Partial<ServiceForm> = {
      destinationContactName: f.originContactName,
      destinationContactPhoneNumber: f.originContactPhoneNumber,
      destinationContactEmailAddress: f.originContactEmailAddress,
      destinationContactPhoneIso: f.originalContactPhoneNumberPhoneIso,
      isDestinationContactExternal: destinationIsExternal,
      destinationContactDocumentType: f.originContactDocumentType,
      destinationContactDocumentNumber: f.originContactDocumentNumber
    };

    this.form.patchValue(patch, { emitEvent: false });
  }

  private mapVehicleTypeInternal(
    input: string | number | null | undefined
  ): string | undefined {
    if (typeof input === 'number') {
      return vehicleChassisType.find((c) => c.value === input)?.name;
    }
    return vehicleChassisType.find((c) => c.real_name === input)?.name;
  }

  private mapFuelTypeInternal(
    input: string | number | null | undefined
  ): string | undefined {
    if (typeof input === 'number') {
      const enumKey = VehicleFuelTypeEnum[
        input
      ] as keyof typeof VehicleFuelTypeEnum;
      return vehicleFuelType.find((f) => f.name === enumKey)?.name;
    }
    return vehicleFuelType.find((f) => f.real_name === input)?.name;
  }

  private mapVertical = (val: number) =>
    this.verticalsTypes.find((v) => v.value === val)?.realName ?? '';

  private mapVerticalValue = (real: string) =>
    this.verticalsTypes.find((v) => v.realName === real)?.value;

  private compareAddress(
    a: ContactInformation,
    b: ContactInformation
  ): boolean {
    return (
      a.address === b.address &&
      a.addressPoint?.Latitude === b.addressPoint?.Latitude &&
      a.addressPoint?.Longitude === b.addressPoint?.Longitude &&
      a.addressDetails === b.addressDetails
    );
  }

  private compareContact(
    a: ContactInformation,
    b: ContactInformation
  ): boolean {
    return (
      a.name === b.name &&
      a.phoneNumber === b.phoneNumber &&
      a.emailAddress === b.emailAddress &&
      a.isExternalContact === b.isExternalContact
    );
  }

  private handleSuccess(): void {
    const key =
      this.type_info === ModalMode.Duplicate
        ? 'modals.add_service.duplicate_success'
        : this.type_info === ModalMode.Edit
          ? 'modals.add_service.edit_success'
          : 'modals.add_service.create_success';

    this.alerts
      .open(this.translate.instant(key), { status: 'success' })
      .subscribe();
    this.finished.emit(true);
    this.off.dismiss();
  }

  private handleError(error?: any): void {
    console.error('Service error details:', error);
    this.alerts
      .open(this.translate.instant('modals.add_service.operation_failed'), {
        status: 'error'
      })
      .subscribe();
  }

  closeCanvas(): void {
    this.off.dismiss();
  }
}
